'use strict';

class S4StylesUtils {

  /**
   * A way to attach styles to any DOM element.
   * @param {DOM Element} element -  The element we want to attach the styles to.
   * @param {Object} stylesObj - The CSS style objects we want to attach
   */
  static attachStyles = (element, stylesObj) => {
    for (const [key, value] of Object.entries(stylesObj)) {
      element.style[key] = value;
    }
  }

  /**
   * Creates and applies the loading animation for the dropin while it is initializing.
   */
   static createLoadingAnimation = () => {

    // This style creates and animates the loading animation of the dropin
    let style = document.createElement('style');
    style.innerHTML = '@keyframes flickerAnimation { \
      0%   { opacity:1; } \
      50%  { opacity:0; } \
      100% { opacity:1; } \
    } \
    @-o-keyframes flickerAnimation{ \
      0%   { opacity:1; } \
      50%  { opacity:0; } \
      100% { opacity:1; } \
    } \
    @-moz-keyframes flickerAnimation{ \
      0%   { opacity:1; } \
      50%  { opacity:0; } \
      100% { opacity:1; } \
    } \
    @-webkit-keyframes flickerAnimation{ \
      0%   { opacity:1; } \
      50%  { opacity:0; } \
      100% { opacity:1; } \
    } \
    .animate-flicker { \
      -webkit-animation: flickerAnimation 1s infinite; \
      -moz-animation: flickerAnimation 1s infinite; \
      -o-animation: flickerAnimation 1s infinite; \
        animation: flickerAnimation 1s infinite; \
    }';
    document.head.appendChild(style);
  }

  /**
   * The Apple Pay Styles explictly to create and attach to the document head
   */
   static createBaseApplePayStyle = () => {
    let style = document.createElement('style');
    style.innerHTML = 'body { --apple-pay-button-width: 100%; --apple-pay-button-height: 48px; }';
    document.head.appendChild(style);
  }

  /**
   * Attach the global fonts from Google Apis that we want for our style.
   */
   static attachGlobalFonts = () => {
    const googleFonts = S4Elements.createElement('link');
    googleFonts.rel = 'preconnect';
    googleFonts.href = 'https://fonts.gstatic.com';

    const googleFontOpenSans = S4Elements.createElement('link');
    googleFontOpenSans.ref = 'stylesheet';
    // googleFontOpenSans.href = 'https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;700&display=swap'
    googleFontOpenSans.href = 'https://fonts.googleapis.com/css2?family=Open+Sans:wght@300;400;600;700;800&display=swap';

    document.head.appendChild(googleFonts);
    document.head.appendChild(googleFontOpenSans);
  }
}

/**
 * Our default colors and styles.
 */
class S4Styles {

  static COLORS = {
    grey: '#666666',
    darkGrey: '#333333',
    lightGrey: '#FCFCFA',
    buttonBorderGrey: '#DDDDDD',
    alert: '#E21E5C',
    blue: '#2253FF'
  }

  static TYPOGRAPHY = {
    subtitle1: {
      'font-family': 'Open Sans',
      'font-size': '12px',
      'line-height': '0.15px',
      'font-weight': 400,
      'color': S4Styles.COLORS.grey
    },
    subtitle2: {
      'font-family': 'Open Sans',
      'font-size': '16px',
      'line-height': '21.79px',
      'font-weight': 400,
      'color': S4Styles.COLORS.grey
    },
    subtitle3: {
      'font-family': 'Open Sans',
      'font-size': '16px',
      'line-height': '21.79px',
      'font-weight': 700,
      'color': S4Styles.COLORS.darkGrey
    },
    small1: {
      'font-family': 'Open Sans',
      'font-size': '9px',
      'font-weight': 400,
      'color': S4Styles.COLORS.blue
    },
    alert: {
      'font-family': 'Open Sans',
      'font-size': '12px',
      'line-height': '0.15px',
      'font-weight': 400,
      'color': S4Styles.COLORS.alert
    }
  }
}

/* eslint-disable valid-jsdoc */

class S4Elements {

  /**
   * Creates a new DOM Element that is ready to be placed onto the DOM.
   * @param {String} type - What type of DOM element do we want to create? Example: div
   * @param {String} id - What is the ID of this element.
   * @param {Object} style - A styles object that matches directly to css.
   * @param {Object} attributes - Attributes that map directly to setAttribute.
   * @returns {DOM Element} - Returns a DOM element of that type, id, styles, and attributes.
   */
   static createElement = (type, id, style, attributes) => {
    const div = document.createElement(type);

    if (id) {
      div.setAttribute('id', id);
    }

    if (attributes) {
      for (const [key, value] of Object.entries(attributes)) {
        div.setAttribute(key, value);
      }
    }

    if (style) {
      S4StylesUtils.attachStyles(div, style);
    }

    return div
  }

  /**
   * Creates a DOM element for text.
   * @param {String} id - What is the ID of the element.
   * @param {String} text - What text do you want to display.
   * @param {Object} outerStyle - Any outstyle that we apply to the surrounding div, but not the text itself.
   * @param {String} styleType - Used as a shortcut to select a global style from the styles.TYPOGRAPHY list.
   * @param {Object} styleOverride - Any styles we want to override on the style typography or type.
   * @returns {DOM Element} Returns a DOM element
   */
   static createTypography = (id, text, outerStyle, styleType, styleOverride) => {
    const div = S4Elements.createElement('div', id, outerStyle);

    const span = S4Elements.createElement('span');

    if (styleType) {
      const selectedStyle = S4Styles.TYPOGRAPHY[styleType];
      if (selectedStyle) {
        S4StylesUtils.attachStyles(span, selectedStyle);
      }
    }

    if (styleOverride) {
      S4StylesUtils.attachStyles(span, styleOverride);
    }

    span.textContent = text;

    div.appendChild(span);
    return div
  }

  /**
   * Create the credit card number Secure Field
   * @param {Object} styleOverride - Any styles we want to override on the style typography or type.
   * @returns {DOM Element} Returns a DOM element
   */
   static createNumberField = (styleOverride) => {
    const style = {
      'border-radius': '6px',
      'height': '48px',
      'background-color': S4Styles.COLORS.lightGrey,
      'border': `${styleOverride?.borderRadius ? styleOverride?.borderRadius : '1px'} solid ${S4Styles.COLORS.buttonBorderGrey}`,
      'overflow': 'hidden',
      'position': 'relative'
    };

    // this ID is very specific
    const field = S4Elements.createElement('div', 's4payments__securefield__number', style);

    if (styleOverride) {
      S4StylesUtils.attachStyles(field, styleOverride);
    }

    return field
  }

  /**
   * Creates the Expiration Date Secure Field
   * @param {Object} styleOverride - Any styles we want to override on the style typography or type.
   * @returns {DOM Element} Returns a DOM element
   */
   static createExpDateField = (styleOverride) => {
    const style = {
      'border-radius': '6px',
      'height': '48px',
      'background-color': S4Styles.COLORS.lightGrey,
      'border': `1px solid ${S4Styles.COLORS.buttonBorderGrey}`,
      'overflow': 'hidden'
    };

    // this ID is very specific
    const field = S4Elements.createElement('div', 's4payments__securefield__exp', style);

    if (styleOverride) {
      S4StylesUtils.attachStyles(field, styleOverride);
    }

    return field
  }

  /**
   * Creates the CVV  Secure Field
   * @param {Object} styleOverride - Any styles we want to override on the style typography or type.
   * @returns {DOM Element} Returns a DOM element
   */
   static createCVVField = (styleOverride) => {
    const style = {
      'border-radius': '6px',
      'height': '48px',
      'background-color': S4Styles.COLORS.lightGrey,
      'border': `1px solid ${S4Styles.COLORS.buttonBorderGrey}`,
      'overflow': 'hidden'
    };

    // this ID is very specific
    const field = S4Elements.createElement('div', 's4payments__securefield__cvv', style);

    if (styleOverride) {
      S4StylesUtils.attachStyles(field, styleOverride);
    }

    return field
  }

  /**
   * Creates a Horizonatal Divider
   * @returns {DOM Element} Returns a DOM element
   */
  static createHorizontalDivider = () => {
    const style = {
      'border': 'none',
      'height': '1px',
      'margin': 0,
      'background-color': 'rgba(0, 0, 0, 0.12)'
    };

    return S4Elements.createElement('hr', null, style)
  }
}

class S4JWT {

  /**
   * Decode a JWT and receive the JSON data
   * @param {String} token - The token value
   * @returns {Object} - The parsed JSON object from the JWT.
   */
  static jwtDecode = (token) => {
    const base64Data = token.split('.')[1];
    const decodedValue = atob(base64Data);
    const json = JSON.parse(decodedValue);
    return json
  }
}

const S4PaymentsWebSDKLoader = (env, debugMode) => {
  return new Promise(async (resolve, reject) => {

    // if S4Payments already exists, don't reload it!
    try {
      if (S4Payments) {
        resolve();
      }
    } catch (error) {
      // this is ok, becuase it means we just need to load the SDK
    }

    // load in S4PaymentsWebSDK from the CDN
    let url = `https://secure.integrations.shift4.com/S4Payments.min.js`;

    switch (env) {
      case 'dev':
        url = `https://secure-${env}.integrations.shift4.com/S4Payments.min.js`;
        // url = `http://localhost:8000/S4Payments.min.js`
        break
      case 'qa':
        url = `https://secure-${env}.integrations.shift4.com/S4Payments.min.js`;
        break
      case 'uat':
        url = `https://secure-${env}.integrations.shift4.com/S4Payments.min.js`;
        break
      default:
        url = `https://secure.integrations.shift4.com/S4Payments.min.js`;
        break;
    }

    let paymentsSDKScript = document.createElement('script');
    paymentsSDKScript.src = url;
    paymentsSDKScript.type = 'text/javascript';
    paymentsSDKScript.onload = () => {
      if (debugMode) {
        console.log('S4PaymentsWebSDK has Loaded from the CDN');
      }

      // setup the bus once
      if (S4Payments) {
        S4Payments.bus.initialize(debugMode);
      } else {
        console.log('Mount failed, please try again.');
      }

      resolve();
    };

    document.head.appendChild(paymentsSDKScript);
  })
};

class Component {

  // Is DEBUG enabled or not
  DEBUG_MODE = false

  decodedToken = undefined
  env = undefined

  configs = undefined
  componentsConfigs = undefined
  globalConfigs = undefined

  /**
   * 
   * @param {*} configs 
   * @param {*} env 
   * @param {*} debugMode 
   * @returns 
   */
  constructor(configs, componentsConfigs, globalConfigs, env, debugMode) {
    console.log("Component");

    this.DEBUG_MODE = debugMode;
    this.env = env;
    this.globalConfigs = globalConfigs;
    this.componentsConfigs = componentsConfigs;
    this.configs = configs;

    if (!configs) {
      console.log('No configs supplied to the component, please try again.');
      return
    }

    if (!configs.token) {
      console.log('No token provided in the config object, please try again.');
      return
    }

    // make sure we can decode the token
    this.decodedToken = S4JWT.jwtDecode(configs.token);

    if (!this.decodedToken) {
      if (this.DEBUG_MODE) {
        console.log(`Token provided is not valid, please try again.`);
      }
      return
    }
  }

  /**
   * 
   * @param {*} container 
   */
  async mount(container) {

    // // find the root injection site
    // const rootInjectionSite = document.getElementById(container)

    // // if we cannot find the injection site, error out
    // if (!rootInjectionSite) {
    //   if (this.DEBUG_MODE) {
    //     console.log(`Container element not found, please try again.`)
    //   }
    //   return
    // }

    // load the S4PaymentsWebSDK - this will only attempt to load it once
    await S4PaymentsWebSDKLoader(this.env, this.DEBUG_MODE);
  }
}

/* eslint-disable valid-jsdoc */

/**
 * Used to send messages to seperated systems but in the same frame. If you want to send across frames,
 * you have to use the BUS.
 */

let receivers = {};

/**
 * Send a message through Pigeon to anyone that is listening.
 * @param {String} key - The key we want to send. Use the pigeon keys enums!
 * @param {Object} payload - The payload you want to send. Can be anything in a JS Object.
 */
const send = (key, payload) => {
  if (receivers[key]) {
    receivers[key].forEach(receiver => {
      receiver(payload);
    });
  }
};

/**
 * Add a listener so we recieve a message when the pigeon event comes to us.
 * @param {String} key - The key we want to send. Use the pigeon keys enums!
 * @param {Function} receiver - Called when an event is fired from send.
 */
const addReceiver = (key, receiver) => {
  if (receivers[key]) {
    receivers[key].push(receiver);
  } else {
    receivers[key] = [receiver];
  }
};

const PIGEON_KEYS = {
  CHANGE_CARD_UI: 'CHANGE_CARD_UI',
  CARD_UPDATE: 'CARD_UPDATE',
  SECURE_FIELD_FOCUS: 'SECURE_FIELD_FOCUS',
  SECURE_FIELD_VALIDATION_ERROR: 'SECURE_FIELD_VALIDATION_ERROR',
  VAULT_RETRIEVED_ALL_CARDS: 'VAULT_RETRIEVED_ALL_CARDS',
  VAULT_CARD_VAULTED: 'VAULT_CARD_VAULTED',
  VAULT_CARD_DELETED: 'VAULT_CARD_DELETED',
  VAULT_CARD_UPDATED: 'VAULT_CARD_UPDATED',
  CARD_TYPE_DETERMINATION: 'CARD_TYPE_DETERMINATION',
  START_WALLET_PROCESS: 'START_WALLET_PROCESS',
  WALLET_PAYMENT: 'WALLET_PAYMENT',
  WALLET_CHANGE: 'WALLET_CHANGE',
  S4PAYMENTS_READY: 'S4PAYMENTS_READY',
  SALE_COMPLETE: 'SALE_COMPLETE',
  TOKENIZE_COMPLETE: 'TOKENIZE_COMPLETE'
};

class CreditCard extends Component {

  // what frames have been initialized. An Object.
  #framesInitialized = {}

  // What frames are we initializing with, defaults
  #framesToInitialize = ['number', 'exp', 'cvv'] 

  /**
   * 
   * @param {*} configs 
   * @param {*} env 
   * @param {*} debugMode 
   * @returns 
   */
  constructor(configs, env, debugMode) {
    super(configs, env, debugMode);

    // if the configs did not validate, do not continue
    if (!super.getConfigs()) {
      return
    }

    console.log("Credit Card");
  }

  /**
 * This is used to initialize the Secure Fields system that is with the s4payments client s4payments-web-sdk.
 */
  initializeSecureFields() {

    if (super.DEBUG_MODE) {
      console.log('initializeSecureFields');
    }

    const token = super.getToken();
    const config = super.getConfigs();

    // if we are going to override the defaults, do it
    this.#framesInitialized = config.fields?.creditCard ? config.fields?.creditCard : this.#framesToInitialize;

    if (token) {

      // create the configuration that we'll use to pass to initiailize the S4Payments library
      const clientConfig = {
        s4paymentsServer: token.s4paymentsServer,
        accessBlock: token.accessBlock,
        secureFieldsServer: token.secureFieldsServer,
        fields: this.#framesInitialized,
        vault: super.getConfigs().vault ? super.getConfigs().vault : false, // a bool if you want to use the vault or not
        debug: super.DEBUG_MODE
      };

      // initialize the web
      S4Payments.client.initialize(clientConfig);
    } else {
      console.log('Token provided is invalid, please try again.');
    }
  }

  /**
   * 
   * @param {*} container 
   */
  async mount(container) {
    await super.mount(container);

    // Send a message when we focus IN or OUT on a Secure Field.
    S4Payments.bus.receive(S4Payments.events.SECURE_FIELD_FOCUS, (payload) => {

      if (super.DEBUG_MODE) {
        console.log('***** S4Payments.bus.receive - SECURE_FIELD_FOCUS *****');
        console.log(payload);
      }

      send(PIGEON_KEYS.SECURE_FIELD_FOCUS, payload);
    }, 'VN_WEB_DROP_IN');   
    
    // When a Secure Field does not validate for any reason
    S4Payments.bus.receive(S4Payments.events.VALIDATION_ERROR, (payload) => {

      if (super.DEBUG_MODE) {
        console.log('***** S4Payments.bus.receive - VALIDATION_ERROR *****');
        console.log(payload);
      }

      if (payload.error) {
        send(PIGEON_KEYS.SECURE_FIELD_VALIDATION_ERROR, payload);

        // if the integrator has setup to receive event notifications
        if (super.getConfigs().events) {
          super.getConfigs().events({ event: PIGEON_KEYS.SECURE_FIELD_VALIDATION_ERROR, ...payload });
        }
      }
    }, 'VN_WEB_DROP_IN');

    // What the credit card type is
    S4Payments.bus.receive(S4Payments.events.CARD_TYPE_DETERMINATION, (payload) => {

      if (super.DEBUG_MODE) {
        console.log('***** S4Payments.bus.receive - CARD_TYPE_DETERMINATION *****');
        console.log(payload);
      }

      send(PIGEON_KEYS.CARD_TYPE_DETERMINATION, payload);
    }, 'VN_WEB_DROP_IN');

    // When a Secure Field frame is done rendering and setting up, then we need to send out letting everyone know we are done. Sends along the frame name and frame ID.
    S4Payments.bus.receive(S4Payments.events.FRAME_INITIALIZED, (payload) => {

      if (super.DEBUG_MODE) {
        console.log('***** S4Payments.bus.receive - FRAME_INITIALIZED *****');
        console.log(payload);
      }

      // what frame name initialized
      this.#framesInitialized[payload.frameName] = true;

      // need to check all the frames to see if they all initialized
      let allInitialized = true;

      // if we find one that is not initialized, then we aren't fully loaded yet.
      for (let i = 0; i < this.#framesToInitialize.length; i++) {
        if (!this.#framesInitialized[this.#framesToInitialize[i]]) {
          allInitialized = false;
          break
        }
      }

      // fully loaded, remove everything!
      if (allInitialized) {
        // displayVault(true)
        // displayWallet(true)
        // removeLoader()

        // if the integrator has setup to receive event notifications
        if (super.getConfigs().events) {
          super.getConfigs().events({ event: PIGEON_KEYS.S4PAYMENTS_READY });
        }
      }
    }, 'VN_WEB_DROP_IN');

    this.initializeSecureFields();
  }
}

class Vault extends Component {

  // An array of all our vaulted cards.
  #vaultedCards

  // the vault root div that the vault attaches to
  #vaultRoot

  /**
   * 
   * @param {*} configs 
   * @param {*} env 
   * @param {*} debugMode 
   * @returns 
   */
  constructor(configs, env, debugMode) {
    super(configs, env, debugMode);

    // if the configs did not validate, do not continue
    if (!super.getConfigs()) {
      return
    }

    console.log("Vault");
  }


  /**
   * Used to display the vault or not that has already been placed on the DOM.
   * @param {Boolean} pDisplay - True if you want to display the vault, false otherwise.
   */
  displayVault = (pDisplay) => {
    if (pDisplay) {
      vaultRoot.style.display = 'block';
    } else {
      vaultRoot.style.display = 'none';
    }
  }

  /**
   * 
   * @param {*} container 
   */
  async mount(container) {
    await super.mount(container);

    // When all cards are retrieved for a specific user
    addReceiver(PIGEON_KEYS.VAULT_RETRIEVED_ALL_CARDS, (payload) => {

      if (super.DEBUG_MODE) {
        console.log('***** PIGEON_KEYS.VAULT_RETRIEVED_ALL_CARDS *****');
        console.log(payload);
      }

      this.#vaultedCards = payload.cards;
      vaultComponents.overrideCardUIState(payload.cards.length > 0 ? cardUI.DEFAULT : cardUI.ADD_CARD);
      paint();
      initializeSecureFields();
      displayVault(true);
      displayWallet(true);
      removeLoader();

      // if the integrator has setup to receive event notifications
      if (super.getConfigs().events) {
        super.getConfigs().events({ event: PIGEON_KEYS.VAULT_RETRIEVED_ALL_CARDS, ...payload });
      }

      // if the integrator has setup to receive event notifications
      if (super.getConfigs().events) {
        super.getConfigs().events({ event: PIGEON_KEYS.S4PAYMENTS_READY });
      }
    });

    // When a card is vaulted successfully
    addReceiver(PIGEON_KEYS.VAULT_CARD_VAULTED, (payload) => {

      if (super.DEBUG_MODE) {
        console.log('***** PIGEON_KEYS.VAULT_CARD_VAULTED *****');
        console.log(payload);
      }

      // need to refetch vaulted cards
      vault.getVaultedCardsByUser(super.getConfigs().token, decodedToken.vaultServer);

      // if the integrator has setup to receive event notifications
      if (super.getConfigs().events) {
        super.getConfigs().events({ event: PIGEON_KEYS.VAULT_CARD_VAULTED, ...payload });
      }
    });

    // When a card has been deleted in the vault successfully.
    addReceiver(PIGEON_KEYS.VAULT_CARD_DELETED, (payload) => {

      if (super.DEBUG_MODE) {
        console.log('***** PIGEON_KEYS.VAULT_CARD_DELETED *****');
        console.log(payload);
      }

      // need to remove it from the local memory
      removeLocalVaultedCard(payload.card.uniqueId);

      // repaint the screen so the card no longer shows up
      paint();

      // if there are no more vaulted cards, we are going to be showing the credit card input fields
      // therefore we need to initialize the secure fields
      if (this.#vaultedCards.length <= 0) {
        initializeSecureFields();
        vaultComponents.overrideCardUIState(cardUI.ADD_CARD);
      }

      // need to refetch vaulted cards
      vault.getVaultedCardsByUser(super.getConfigs().token, decodedToken.vaultServer);

      // if the integrator has setup to receive event notifications
      if (super.getConfigs().events) {
        super.getConfigs().events({ event: PIGEON_KEYS.CARD_UPDATE, ...payload });
      }
    });

    // listen for all card event updates
    addReceiver(PIGEON_KEYS.CARD_UPDATE, (payload) => {

      if (super.DEBUG_MODE) {
        console.log('***** PIGEON_KEYS.CARD_UPDATE *****');
        console.log(payload);
      }

      if (payload.status === 'DELETED') {
        if (super.getConfigs().vault) {
          vault.deleteCard(payload.id, super.getConfigs().token, decodedToken.vaultServer);
        }
      } else if (payload.status === 'SELECTED') {
        if (super.getConfigs().vault) {
          updateDefaultLocalVaultedCard(payload.id);
          vault.updateCard({uniqueId: payload.id, default: true}, super.getConfigs().token, decodedToken.vaultServer);
        }
      }

      // if the integrator has setup to receive event notifications
      if (super.getConfigs().events) {
        super.getConfigs().events({ event: PIGEON_KEYS.CARD_UPDATE, ...payload });
      }
    });

    // listen for when a card has been updated on the server
    addReceiver(PIGEON_KEYS.VAULT_CARD_UPDATED, (payload) => {

      if (super.DEBUG_MODE) {
        console.log('***** PIGEON_KEYS.VAULT_CARD_UPDATED *****');
        console.log(payload);
      }

      // if the integrator has setup to receive event notifications
      if (super.getConfigs().events) {
        super.getConfigs().events({ event: PIGEON_KEYS.VAULT_CARD_UPDATED, ...payload });
      }
    });

    // Anytime the card UI changes; such as when changing from DELETE mode to DEFAULT mode.
    addReceiver(PIGEON_KEYS.CHANGE_CARD_UI, (payload) => {

      if (super.DEBUG_MODE) {
        console.log('***** PIGEON_KEYS.CHANGE_CARD_UI *****');
        console.log(payload);
      }

      // only handle the client init for the library if we are showing the secure fields
      if (payload.status === cardUI.ADD_CARD) {
        initializeSecureFields();
      }

      // if the integrator has setup to receive event notifications
      if (super.getConfigs().events) {
        super.getConfigs().events({ event: PIGEON_KEYS.CHANGE_CARD_UI, ...payload });
      }
    });

    this.#vaultRoot = S4Elements.createElement('div', 's4payments-drop-in-vault-root');

    // initially hide it until we are ready to display it
    this.displayVault(false);

    container.appendChild(this.#vaultRoot);
  }
}

class Wallet extends Component {

  /**
   * 
   * @param {*} configs 
   * @param {*} env 
   * @param {*} debugMode 
   * @returns 
   */
  constructor(configs, env, debugMode) {
    super(configs, env, debugMode);

    // if the configs did not validate, do not continue
    if (!super.getConfigs()) {
      return
    }

    console.log("Wallet");
  }

  /**
   * 
   * @param {*} container 
   */
  async mount(container) {
    await super.mount(container);

    // An Apple Pay or Google Pay wallet payment
    addReceiver(PIGEON_KEYS.WALLET_PAYMENT, (payload) => {

      if (super.DEBUG_MODE) {
        console.log('***** PIGEON_KEYS.WALLET_PAYMENT *****');
        console.log(payload);
      }

      // if the integrator has setup to receive event notifications
      if (super.getConfigs().events) {
        super.getConfigs().events({ event: PIGEON_KEYS.WALLET_PAYMENT, ...payload });
      }
    });

    // An Apple Pay or Google Pay wallet change event like 'cancel' or 'not_supported'
    addReceiver(PIGEON_KEYS.WALLET_CHANGE, (payload) => {

      if (super.DEBUG_MODE) {
        console.log('***** PIGEON_KEYS.WALLET_CHANGE *****');
        console.log(payload);
      }

      // if the integrator has setup to receive event notifications
      if (super.getConfigs().events) {
        super.getConfigs().events({ event: PIGEON_KEYS.WALLET_CHANGE, ...payload });
      }
    });

    // Called when a payment is started from the wallet; this would be apple pay or google pay.
    addReceiver(PIGEON_KEYS.START_WALLET_PROCESS, (payload) => {

      if (super.DEBUG_MODE) {
        console.log('***** PIGEON_KEYS.START_WALLET_PROCESS *****');
        console.log(payload);
      }

      // if the integrator has setup to receive event notifications
      if (super.getConfigs().events) {
        super.getConfigs().events({ event: PIGEON_KEYS.START_WALLET_PROCESS, ...payload });
      }
    });
  }
}

/* eslint-disable valid-jsdoc */

class S4Loaders {

  /**
   * Makes a single loader bar
   * @param {Float} count - When does the animation fire.
   * @returns {DOM Element} - Returns a DOM element for a single loader bar.
   */
  static makeLoaderBar(count) {

    const div = document.createElement('div');
    const style = {
      width: '100%',
      height: '48px',
      'border-radius': '6px',
      background: 'rgba(234, 242, 255',
      'margin-bottom': '16px',
      animationDelay: `${count}s`
    };

    S4StylesUtils.attachStyles(div, style);
    div.classList.add('animate-flicker');
    return div
  }

  /**
   * Makes a loader for the Dropin as a whole.
   * @returns {DOM Element} - Returns a DOM element for the loader as a whole.
   */
   static makeLoader = () => {
    const div = S4Elements.createElement('div', 'shift4-loader');
    const bar1 = S4Loaders.makeLoaderBar(0.75);
    const bar2 = S4Loaders.makeLoaderBar(0.5);
    const bar3 = S4Loaders.makeLoaderBar(0.25);
    const bar4 = S4Loaders.makeLoaderBar(0);
    div.prepend(bar1);
    div.prepend(bar2);
    div.prepend(bar3);
    div.prepend(bar4);
    return div
  }

  /**
   * Removes the loader from the DOM.
   */
   static removeLoader = () => {
    const div = document.getElementById('shift4-loader');
    if (div) {
      div.parentElement.removeChild(div);
    }
  }
}

class S4Constants {
  static mode = {
    EDIT: 'EDIT',
    SELECT: 'SELECT'
  }

  static cardUI = {
    DEFAULT: 'DEFAULT', // Default card UI state
    ADD_CARD: 'ADD_CARD', // Add a card UI state
    DELETE_CARD: 'DELETE_CARD' // Delete a card UI state
  }
}

/* eslint-disable valid-jsdoc */

class S4UtilsDOM {

  /**
   * Given a parent element, remove all the children and replace it with the supplied element.
   * @param {DOM Element} parentElement - The parent DOM element to remove all the children.
   * @param {DOM Element} replacingElement - What child element you are attaching to the parent.
   */
  static replaceChildren = (parentElement, replacingElement) => {
    while (parentElement.firstChild) {
      parentElement.removeChild(parentElement.lastChild);
    }

    parentElement.appendChild(replacingElement);
  }
}

/**
 * A vanilla ajax helper function.
 * @param {Object} options - type, url, headers, isFOrmData, data
 * @returns {Promise} - Returns a promise
 */
const ajax = options => {

  return new Promise((resolve, reject) => {

    let xhr = new XMLHttpRequest();

    xhr.open(options.type, options.url, true);

    for (const i in options.headers) {
      if (Object.prototype.hasOwnProperty.call(options.headers, i)) {
        xhr.setRequestHeader(i, options.headers[i]);
      }
    }

    xhr.onerror = function () {
      reject({
        response: xhr.response,
        status: xhr.status
      });
    };

    xhr.onreadystatechange = function () {
      if (this.readyState === XMLHttpRequest.DONE) {
        resolve({
          response: xhr.response,
          status: xhr.status
        });
      }
    };

    if (options.isFormData) {
      let urlEncodedData = '';
      let urlEncodedDataPairs = [];

      for (const name in options.data) {
        if (Object.prototype.hasOwnProperty.call(options.data, name)) {
          urlEncodedDataPairs.push(`${encodeURIComponent(name)}=${encodeURIComponent(options.data[name])}`);
        }
      }

      urlEncodedData = urlEncodedDataPairs.join('&').replace(/%20/g, '+');

      xhr.send(urlEncodedData);

    } else {
      xhr.send(JSON.stringify(options.data));
    }
  })
};

/* eslint-disable valid-jsdoc */

class S4Vault {

  /**
   * Get all the vaulted cards for a specific user. The token is what identifies the user.
   * @param {String} token - The Shift4 Token from preauthorize
   * @param {String} vaultServer - The VNAPI Vault Server
   */
   static getVaultedCardsByUser = (token, vaultServer) => {
    ajax({
      type: 'GET',
      url: `${vaultServer}/vault/cards`,
      headers: {
        'Authorization': `Bearer ${token}`
      }
    }).then((responseObj) => {
      send(PIGEON_KEYS.VAULT_RETRIEVED_ALL_CARDS, {'cards': JSON.parse(responseObj.response) });
    }).catch((e) => {
      console.log(e);
    });
  }

  /**
   * Convert a Shift4 Card type to a regular nice name of the card type.
   * @param {String} cardType - The card type: VS, AX, NS, MC
   * @returns {String} Returns the nice name. (visa, amex, discover, mastercard)
   */
   static getConvertedShift4CardType = (cardType) => {
    switch (cardType) {
      case 'VS':
        return 'visa'
      case 'AX':
        return 'amex'
      case 'NS':
        return 'discover'
      case 'MC':
        return 'mastercard'
      default:
    }
  }

  /**
   * Vault a card against VNAPI Global.
   * @param {Object} shift4Card - The tokenized card from Shift4
   * @param {String} token - The users Shift4 Bearer Token
   * @param {String} vaultServer - The vault server for VNAPI
   * @returns {Promise} - Returns a promise for when you attempt to vault a card
   */
   static vaultCard = (shift4Card, token, vaultServer, transactionId = null) => {

    return new Promise((resolve, reject) => {

      // convert shift4 card obj into what we need for VNAPI
      const splitExp = shift4Card.expiration.split('/');

      const cardObj = {
        cardType: S4Vault.getConvertedShift4CardType(shift4Card.cardType),
        uToken: shift4Card.token,
        uniqueId: shift4Card.uniqueId ? shift4Card.uniqueId : shift4Card.token,
        expMonth: splitExp[0],
        expYear: splitExp[1],
        lastFour: shift4Card.lastFour,
        default: true,
        reference: shift4Card.reference,
        bin: shift4Card.bin,
        transactionId: transactionId,
        threeDS: shift4Card.threeDS,
        risk: shift4Card.risk
      };

      ajax({
        type: 'POST',
        url: `${vaultServer}/vault/card`,
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
          'Authorization': `Bearer ${token}`
        },
        data: cardObj
      }).then((responseObj) => {
        send(PIGEON_KEYS.VAULT_CARD_VAULTED, {'card': JSON.parse(responseObj.response) });
        resolve(JSON.parse(responseObj.response));
      }).catch((e) => {
        console.log(e);
        reject(e);
      });
    })
  }

  /**
   * Updates a card that has already been vaulted.
   * @param {Object} data - The card data we want to update
   * @param {String} token - The users Shift4 Bearer Token
   * @param {String} vaultServer - The vault server for VNAPI
   */
   static updateCard = (data, token, vaultServer) => {
    return new Promise((resolve, reject) => {
      ajax({
        type: 'PUT',
        url: `${vaultServer}/vault/card`,
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
          'Authorization': `Bearer ${token}`
        },
        data: data
      }).then((responseObj) => {
        send(PIGEON_KEYS.VAULT_CARD_UPDATED, {'card': JSON.parse(responseObj.response) });
        resolve(JSON.parse(responseObj.response));
      }).catch((e) => {
        console.log(e);
      });
    })
  }

  /**
   * Deletes a card in the vault.
   * @param {String} cardId - The card ID we want to delete. Must belong to the user. :)
   * @param {String} token - The users Shift4 Bearer Token
   * @param {String} vaultServer - The vault server for VNAPI
   */
  static deleteCard = (cardId, token, vaultServer) => {
    ajax({
      type: 'DELETE',
      url: `${vaultServer}/vault/card?uniqueId=${cardId}`,
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
        'Authorization': `Bearer ${token}`
      }
    }).then((responseObj) => {
      send(PIGEON_KEYS.VAULT_CARD_DELETED, {'card': JSON.parse(responseObj.response) });
    }).catch((e) => {
      console.log(e);
    });
  }
}

const EN_US = {
  credit_cards: 'Credit Cards',
  card_number: 'Card Number',
  exp_month_year: 'Exp MM/YY',
  cvv_code: 'CVV Code',
  exp_date: 'Exp Date',
  cvv: 'CVV',
  add_a_credit_card: 'Add a Credit Card',
  apple_pay: 'Apple Pay',
  add_a_card: 'Add a Card',
  card_declined: 'That card was declined'
};

const ES_ES = {
  credit_cards: 'tarjetas de crédito',
  card_number: 'número de tarjeta',
  exp_month_year: 'Exp MM/YY',
  cvv_code: 'CVV Código',
  exp_date: 'Exp Fecho',
  cvv: 'CVV',
  add_a_credit_card: 'Añade una tarjeta de crédito',
  apple_pay: 'Apple Pay',
  add_a_card: 'añade una tarjeta',
  card_declined: 'Esa tarjeta fue rechazada'
};

/* eslint-disable valid-jsdoc */

/**
 * locale is only set currently through what you pass in.
 */
class S4Locale {

  constructor() {

  }

  /**
  * Check to see if we support the language passed in.
  * @param {String} lang - The langauge to check to see if we support. We currently only support en-us.
  * @returns {Boolean} - Returns true if we support it, false othewise.
  */
  static isSupported = (locale) => {
    switch (locale.toLowerCase()) {
      case 'en-US':
        return true
      case 'es-es':
        return true
      default:
        return false
    }
  }

  /**
   * Get the appropriate language file from the language that was set.
   * @param {String} languageToCheck - What language to check in en-us format.
   * @returns {Object} - Returns the language object with all the translated words.
   */
  static getLocalization = (locale) => {
    switch (locale.toLowerCase()) {
      case 'en-us':
        return EN_US
      case 'es-es':
        return ES_ES
      default:
        return EN_US
    }
  }
}

/**
 * SVG's!!!
 */
class S4SVGS {

  static create(svg, style) {
    const div = S4Elements.createElement('div', null, style);
    div.innerHTML = svg;
    return div
  }

  static plusIcon = (fill = 'none') => {
    const svg = `<svg width="20" height="20" viewBox="0 0 20 20" fill="${fill}" xmlns="http://www.w3.org/2000/svg">` +
                `<path d="M17.5 0H2.5C1.125 0 0 1.125 0 2.5V17.5C0 18.875 1.125 20 2.5 20H17.5C18.875 20 20 18.875 20 17.5V2.5C20 1.125 18.875 0 17.5 0ZM16.25 11.25H11.25V16.25H8.75V11.25H3.75V8.75H8.75V3.75H11.25V8.75H16.25V11.25Z" fill="${fill}"/>` +
                '</svg>';

    return S4SVGS.create(svg)
  }

  static visaIcon = () => {
    const svg = '<svg width="34" height="24" viewBox="0 0 34 24" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<rect x="0.5" y="0.5" width="33" height="23" rx="3.5" fill="white" stroke="#DDDDDD"/>' +
    '<path fill-rule="evenodd" clip-rule="evenodd" d="M10.7503 15.8582H8.69051L7.14596 9.79237C7.07265 9.51334 6.91699 9.26667 6.68802 9.1504C6.11659 8.85823 5.48692 8.62571 4.8 8.50843V8.2749H8.11808C8.57603 8.2749 8.91948 8.62571 8.97673 9.03313L9.77813 13.4086L11.8369 8.2749H13.8394L10.7503 15.8582ZM14.9842 15.8582H13.039L14.6408 8.2749H16.586L14.9842 15.8582ZM19.1027 10.3757C19.1599 9.96728 19.5034 9.73374 19.9041 9.73374C20.5338 9.67511 21.2197 9.79238 21.7921 10.0835L22.1356 8.45081C21.5631 8.21727 20.9335 8.1 20.362 8.1C18.474 8.1 17.1002 9.15041 17.1002 10.6082C17.1002 11.7173 18.0733 12.2996 18.7603 12.6504C19.5034 13.0002 19.7896 13.2338 19.7324 13.5835C19.7324 14.1082 19.1599 14.3418 18.5885 14.3418C17.9016 14.3418 17.2147 14.1669 16.586 13.8747L16.2426 15.5085C16.9295 15.7996 17.6726 15.9169 18.3596 15.9169C20.4765 15.9745 21.7921 14.9251 21.7921 13.35C21.7921 11.3665 19.1027 11.2502 19.1027 10.3757ZM28.6 15.8582L27.0554 8.2749H25.3964C25.0529 8.2749 24.7095 8.50843 24.595 8.85823L21.7349 15.8582H23.7374L24.1371 14.7502H26.5975L26.8265 15.8582H28.6ZM25.6826 10.3171L26.254 13.1751H24.6523L25.6826 10.3171Z" fill="#172B85"/>' +
    '</svg>';
    return S4SVGS.create(svg)
  }

  static applePayIcon = () => {
    const svg = '<svg width="34" height="24" viewBox="0 0 34 24" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<rect x="0.5" y="0.5" width="33" height="23" rx="3.5" fill="white" stroke="#DDDDDD"/>' +
    '<path fill-rule="evenodd" clip-rule="evenodd" d="M9.44921 8.34316C9.16382 8.69506 8.70721 8.97261 8.2506 8.93296C8.19353 8.45715 8.41707 7.95161 8.67867 7.63936C8.96406 7.27755 9.46348 7.01983 9.86777 7C9.91533 7.49563 9.72983 7.98135 9.44921 8.34316ZM9.86297 9.02712C9.46071 9.003 9.09366 9.15319 8.79718 9.2745C8.60639 9.35256 8.44483 9.41867 8.32191 9.41867C8.18397 9.41867 8.01574 9.34903 7.82685 9.27084L7.82685 9.27084C7.57935 9.16838 7.29638 9.05124 6.99964 9.05686C6.31948 9.06677 5.68688 9.46823 5.33967 10.1076C4.62621 11.3863 5.15417 13.2796 5.84384 14.3205C6.18155 14.8359 6.58584 15.4009 7.11855 15.3811C7.35291 15.3719 7.5215 15.2973 7.69597 15.2202C7.89683 15.1314 8.10549 15.0391 8.43131 15.0391C8.74582 15.0391 8.94536 15.129 9.1369 15.2152C9.31903 15.2973 9.49393 15.376 9.75358 15.3712C10.3053 15.3613 10.6525 14.8557 10.9902 14.3403C11.3547 13.7871 11.5148 13.2471 11.5391 13.1652L11.542 13.1557C11.5414 13.1551 11.5369 13.153 11.5289 13.1492C11.4071 13.0911 10.476 12.6469 10.467 11.4557C10.4581 10.4559 11.2056 9.94935 11.3233 9.86961L11.3233 9.8696C11.3304 9.86476 11.3353 9.86149 11.3374 9.85978C10.8618 9.12625 10.1198 9.04695 9.86297 9.02712ZM13.6824 15.3167V7.5898H16.4649C17.9013 7.5898 18.9049 8.62071 18.9049 10.1274C18.9049 11.6341 17.8822 12.675 16.4268 12.675H14.8334V15.3167H13.6824ZM14.8333 8.60088H16.1603C17.1592 8.60088 17.7299 9.15599 17.7299 10.1324C17.7299 11.1088 17.1592 11.6688 16.1556 11.6688H14.8333V8.60088ZM22.7053 14.3898C22.4009 14.9945 21.7302 15.3761 21.0072 15.3761C19.9371 15.3761 19.1903 14.712 19.1903 13.7108C19.1903 12.7196 19.9133 12.1496 21.2498 12.0653L22.6862 11.9761V11.5499C22.6862 10.9204 22.2915 10.5784 21.5875 10.5784C21.0072 10.5784 20.5839 10.8907 20.4983 11.3665H19.4614C19.4947 10.3653 20.3984 9.63675 21.6208 9.63675C22.9383 9.63675 23.7945 10.3554 23.7945 11.4706V15.3167H22.729V14.3898H22.7053ZM21.3163 14.4592C20.7027 14.4592 20.3127 14.1519 20.3127 13.6811C20.3127 13.1954 20.6885 12.9129 21.4067 12.8683L22.6861 12.784V13.2202C22.6861 13.9438 22.0964 14.4592 21.3163 14.4592ZM27.3284 15.619C26.867 16.9721 26.3391 17.4181 25.2166 17.4181C25.131 17.4181 24.8456 17.4082 24.779 17.3884V16.4616C24.8503 16.4715 25.0263 16.4814 25.1167 16.4814C25.6256 16.4814 25.911 16.2584 26.087 15.6785L26.1916 15.3365L24.2415 9.7111H25.4449L26.8004 14.2759H26.8242L28.1798 9.7111H29.3499L27.3284 15.619Z" fill="black"/>' +
    '</svg>';
    return S4SVGS.create(svg)
  }

  static mastercardIcon = () => {
    const svg = '<svg width="34" height="24" viewBox="0 0 34 24" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<rect x="0.5" y="0.5" width="33" height="23" rx="3.5" fill="white" stroke="#DDDDDD"/>' +
    '<path fill-rule="evenodd" clip-rule="evenodd" d="M17.179 16.8294C15.9949 17.8275 14.459 18.43 12.7807 18.43C9.03582 18.43 6 15.4303 6 11.73C6 8.02966 9.03582 5.02997 12.7807 5.02997C14.459 5.02997 15.9949 5.63247 17.179 6.63051C18.363 5.63247 19.8989 5.02997 21.5773 5.02997C25.3221 5.02997 28.358 8.02966 28.358 11.73C28.358 15.4303 25.3221 18.43 21.5773 18.43C19.8989 18.43 18.363 17.8275 17.179 16.8294Z" fill="#ED0006"/>' +
    '<path fill-rule="evenodd" clip-rule="evenodd" d="M17.179 16.8294C18.6369 15.6005 19.5614 13.7719 19.5614 11.73C19.5614 9.68801 18.6369 7.85941 17.179 6.63051C18.363 5.63247 19.8989 5.02997 21.5773 5.02997C25.3221 5.02997 28.3579 8.02966 28.3579 11.73C28.3579 15.4303 25.3221 18.43 21.5773 18.43C19.8989 18.43 18.363 17.8275 17.179 16.8294Z" fill="#F9A000"/>' +
    '<path fill-rule="evenodd" clip-rule="evenodd" d="M17.179 16.8294C18.6369 15.6005 19.5614 13.7719 19.5614 11.73C19.5614 9.68804 18.6369 7.85945 17.179 6.63054C15.7211 7.85945 14.7966 9.68804 14.7966 11.73C14.7966 13.7719 15.7211 15.6005 17.179 16.8294Z" fill="#FF5E00"/>' +
    '</svg>';
    return S4SVGS.create(svg)
  }

  static discoverIcon = () => {
    const svg = '<svg width="34" height="24" viewBox="0 0 34 24" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<rect x="0.5" y="0.5" width="33" height="23" rx="3.5" fill="white" stroke="#DDDDDD"/>' +
    '<path d="M14 23L33 17.25V20C33 21.6569 31.6569 23 30 23H14Z" fill="#FD6020"/>' +
    '<path fill-rule="evenodd" clip-rule="evenodd" d="M29.3937 9.11084C30.439 9.11084 31.0139 9.59438 31.0139 10.5077C31.0662 11.2062 30.5958 11.7972 29.9686 11.9046L31.3797 13.8925H30.2822L29.0801 11.9584H28.9756V13.8925H28.0871V9.11084H29.3937ZM28.9756 11.3137H29.2369C29.8118 11.3137 30.0731 11.045 30.0731 10.5615C30.0731 10.1317 29.8118 9.86304 29.2369 9.86304H28.9756V11.3137ZM25.0034 13.8925H27.5122V13.0866H25.8919V11.7972H27.4599V10.9913H25.8919V9.91674H27.5122V9.11084H25.0034V13.8925ZM22.3902 12.3345L21.1881 9.11084H20.2474L22.1812 14H22.6515L24.5853 9.11084H23.6446L22.3902 12.3345ZM11.7805 11.5286C11.7805 12.8717 12.8258 14 14.1324 14C14.5505 14 14.9164 13.8925 15.2822 13.7314V12.6568C15.0209 12.9792 14.655 13.1941 14.2369 13.1941C13.4007 13.1941 12.7212 12.5494 12.7212 11.6897V11.5823C12.669 10.7227 13.3484 9.97048 14.1847 9.91675C14.6028 9.91675 15.0209 10.1317 15.2822 10.454V9.37948C14.9686 9.16458 14.5505 9.11085 14.1847 9.11085C12.8258 9.0034 11.7805 10.1317 11.7805 11.5286ZM10.1603 10.9376C9.63762 10.7227 9.48082 10.6152 9.48082 10.3466C9.53309 10.0242 9.79441 9.75557 10.108 9.8093C10.3693 9.8093 10.6306 9.97048 10.8397 10.1854L11.3101 9.54066C10.9442 9.2183 10.4739 9.00339 10.0035 9.00339C9.27176 8.94967 8.64459 9.54066 8.59232 10.2928V10.3466C8.59232 10.9913 8.85365 11.3674 9.68988 11.636C9.89894 11.6897 10.108 11.7972 10.3171 11.9046C10.4739 12.0121 10.5784 12.1733 10.5784 12.3882C10.5784 12.7643 10.2648 13.0866 9.95121 13.0866H9.89894C9.48082 13.0866 9.11497 12.818 8.95818 12.4419L8.38326 13.0329C8.69685 13.6239 9.32403 13.9463 9.95121 13.9463C10.7874 14 11.4669 13.3553 11.5191 12.4956V12.3345C11.4669 11.6897 11.2056 11.3674 10.1603 10.9376ZM7.12892 13.8925H8.01742V9.11084H7.12892V13.8925ZM3 9.11086H4.30662H4.56794C5.8223 9.16458 6.81532 10.2391 6.76306 11.5286C6.76306 12.227 6.44947 12.8717 5.92682 13.3553C5.45644 13.7314 4.88153 13.9463 4.30662 13.8926H3V9.11086ZM4.14983 13.0866C4.56794 13.1404 5.03833 12.9792 5.35191 12.7105C5.6655 12.3882 5.8223 11.9584 5.8223 11.4748C5.8223 11.045 5.6655 10.6152 5.35191 10.2928C5.03833 10.0242 4.56794 9.86302 4.14983 9.91674H3.8885V13.0866H4.14983Z" fill="black"/>' +
    '<path fill-rule="evenodd" clip-rule="evenodd" d="M17.9477 9C16.6411 9 15.5435 10.0745 15.5435 11.4714C15.5435 12.8146 16.5888 13.9429 17.9477 13.9966C19.3066 14.0503 20.3519 12.9221 20.4042 11.5252C20.3519 10.1283 19.3066 9 17.9477 9V9Z" fill="#FD6020"/>' +
    '</svg>';
    return S4SVGS.create(svg)
  }

  static amexIcon = () => {
    const svg = '<svg width="34" height="24" viewBox="0 0 34 24" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<rect x="0.5" y="0.5" width="33" height="23" rx="3.5" fill="#1F72CD" stroke="#DDDDDD"/>' +
    '<path fill-rule="evenodd" clip-rule="evenodd" d="M6.0954 8.5L2.91429 15.7467H6.72253L7.19464 14.5913H8.27378L8.74589 15.7467H12.9377V14.8649L13.3112 15.7467H15.4795L15.853 14.8462V15.7467H24.5708L25.6309 14.6213L26.6235 15.7467L31.1011 15.7561L27.91 12.1436L31.1011 8.5H26.6929L25.661 9.60463L24.6997 8.5H15.2159L14.4015 10.3704L13.568 8.5H9.76772V9.35186L9.34497 8.5H6.0954ZM6.83228 9.52905H8.68859L10.7986 14.4431V9.52905H12.8321L14.4619 13.0524L15.9639 9.52905H17.9873V14.7291H16.7561L16.746 10.6544L14.9511 14.7291H13.8498L12.0448 10.6544V14.7291H9.51202L9.03185 13.5633H6.43767L5.9585 14.728H4.60146L6.83228 9.52905ZM24.1198 9.52905H19.1136V14.726H24.0423L25.6309 13.0036L27.162 14.726H28.7626L26.4362 12.1426L28.7626 9.52905H27.2315L25.651 11.2316L24.1198 9.52905ZM7.73531 10.4089L6.88063 12.4856H8.58899L7.73531 10.4089ZM20.3499 11.555V10.6057V10.6048H23.4736L24.8366 12.1229L23.4132 13.6493H20.3499V12.613H23.081V11.555H20.3499Z" fill="white"/>' +
    '</svg>';
    return S4SVGS.create(svg)
  }

  static cancelIcon = () => {
    const svg = '<svg viewBox="0 0 24 24"><path d="M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm5 13.59L15.59 17 12 13.41 8.41 17 7 15.59 10.59 12 7 8.41 8.41 7 12 10.59 15.59 7 17 8.41 13.41 12 17 15.59z"></path></svg>';
    const div = S4SVGS.create(svg);
    S4StylesUtils.attachStyles(div, {
      'fill': S4Styles.COLORS.grey,
      'font-size': '25px',
      'width': '1em',
      'height': '1em'
    });
    return div
  }

  static radioButtonUncheckedIcon = () => {
    const svg = '<svg viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z"></path></svg>';
    const div = S4SVGS.create(svg);
    S4StylesUtils.attachStyles(div, {
      'fill': S4Styles.COLORS.blue,
      'font-size': '25px',
      'width': '1em',
      'height': '1em'
    });
    return div
  }

  static radioButtonCheckedIcon = () => {
    const svg = '<svg viewBox="0 0 24 24"><path d="M12 7c-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5-2.24-5-5-5zm0-5C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z"></path></svg>';
    const div = S4SVGS.create(svg);
    S4StylesUtils.attachStyles(div, {
      'fill': S4Styles.COLORS.blue,
      'font-size': '25px',
      'width': '1em',
      'height': '1em'
    });
    return div
  }

  static deleteIcon = () => {
    const svg = '<svg width="14" height="18" viewBox="0 0 14 18" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<path d="M11 6V16H3V6H11ZM9.5 0H4.5L3.5 1H0V3H14V1H10.5L9.5 0ZM13 4H1V16C1 17.1 1.9 18 3 18H11C12.1 18 13 17.1 13 16V4Z" fill="white"/>' +
    '</svg>';
    return S4SVGS.create(svg)
  }

  static subtractIcon = () => {
    const svg = '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<path fill-rule="evenodd" clip-rule="evenodd" d="M10 20C15.5228 20 20 15.5228 20 10C20 4.47715 15.5228 0 10 0C4.47715 0 0 4.47715 0 10C0 15.5228 4.47715 20 10 20ZM13.4937 5.625L14.375 6.50625L10.8813 10L14.375 13.4937L13.4937 14.375L10 10.8813L6.50625 14.375L5.625 13.4937L9.11875 10L5.625 6.50625L6.50625 5.625L10 9.11875L13.4937 5.625Z" fill="white"/>' +
    '</svg>';
    return S4SVGS.create(svg)
  }
}

// integrators have sudo-css code, this is a conversion map to true CSS
const styleLookup = {
  'borderRadius': 'border-radius',
  'colorText': 'color',
  'colorPrimary': 'border-color',
  'spacingTab': 'margin-bottom',
  'labelWeight': 'font-weight',
  'colorSelective': 'background-color', // color for selected card
  'colorSelectiveText': 'color',
  'colorSelectiveSecondary': 'border-color',
  'colorSecondary': 'color', // color for add button on selected card
  'colorDestructive': 'color',
  'colorDestructiveSecondary': 'color',
  'colorDestructiveText': 'text-color'
};

// input fields allowed styles
const inputFields = new Set([
  'borderRadius', 'colorText', 'colorPrimary', 'spacingTab'
]);

const labels = new Set([
  'labelWeight'
]);

const selectedCard = new Set([
  'colorSelective', 'colorSelectiveText', 'colorSelectiveSecondary', 'colorPrimary'
]);

const deleteCard = new Set([
  'colorDestructive', 'colorDestructiveSecondary', 'colorDestructiveText', 'colorPrimary'
]);

const addCard = new Set([
  'colorSecondary'
]);

const defaultStyle = {
  'border-radius': '6px',
  'height': '48px',
  'background-color': S4Styles.COLORS.lightGrey,
  'border': `1px solid ${S4Styles.COLORS.buttonBorderGrey}`,
  'overflow': 'hidden'
};

let selectedStyle = {
  'border-radius': '6px',
  'height': '48px',
  'display': 'flex',
  'align-items': 'center',
  'background-color': S4Styles.COLORS.lightGrey,
  'border': `1px solid ${S4Styles.COLORS.buttonBorderGrey}`
};

// takes integrator sudo-css and turns give object that have true CSS
const getStyleWithOverride = (override) => {
  let inputFieldStyles = {};
  let labelStyles = {};
  let deleteCardStyle = {};
  let addCardStyle = {};

  for (const style in override) {
    if (Object.hasOwnProperty.call(override, style)) {
      if (inputFields.has(style)) {
        inputFieldStyles[styleLookup[style]] = override[style];
      }
      if (labels.has(style)) {
        labelStyles[styleLookup[style]] = override[style];
      }
      if (selectedCard.has(style)) {
        selectedStyle[styleLookup[style]] = override[style];
      }
      if (deleteCard.has(style)) {
        deleteCardStyle[styleLookup[style]] = override[style];
      }
      if (addCard.has(style)) {
        addCardStyle[styleLookup[style]] = override[style];
      }
    }
  }

  return {
    'inputFields': inputFieldStyles,
    'label': labelStyles,
    'selectedCard': selectedStyle,
    'deleteCard': deleteCardStyle,
    'addCard': addCardStyle
  }
};

/* eslint-disable valid-jsdoc */

class S4VaultComponents {

  // the language that was configured in the drop in.
  language

  // what mode are we in - from constants.mode
  currentMode

  // The root DOM element that the vault is
  root

  // An array of card objects that we received from the API for the vault.
  cards

  // The selected card ID
  selectedCard

  // The card that is selected to be deleted (user is still trying to decide if they want to delete it or not.)
  deletedCardSelected

  // A dictionary keyed by secure field names - Value is a boolean if that field is valid or not
  fieldsValid = {}

  // The current card UI state
  currentCardUIState = S4Constants.cardUI.DEFAULT

  // Are we in debug mode or not
  DEBUG_MODE = false

  styleOverride = {}

  constructor(STYLE_OVERRIDE) {
    this.styleOverride = getStyleWithOverride(STYLE_OVERRIDE);
  }

  /**
   * Resets the vault to the base values
   */
  reset = () => {
    this.deletedCardSelected = null;
    this.currentCardUIState = S4Constants.cardUI.DEFAULT;
  }

  /**
   * Retrieves an SVG of the card type logo, null if we don't have the art.
   * @param {String} cardType - The card type (visa, american-express, amex, discover, mastercard)
   * @returns {SVG} - Returns an SVG of the card type logo if we have it.
   */
  getCardIcon = (cardType) => {
    let cardIcon;
    switch (cardType) {
      case 'visa':
        cardIcon = S4SVGS.visaIcon();
        break
      case 'amex':
      case 'american-express':
        cardIcon = S4SVGS.amexIcon();
        break
      case 'discover':
        cardIcon = S4SVGS.discoverIcon();
        break
      case 'mastercard':
        cardIcon = S4SVGS.mastercardIcon();
        break
      default:
        cardIcon = null;
    }
    return cardIcon
  }

  /**
   * If we get a validation error from a Secure Field, we need to update the styling around the Secure Field.
   * @param {Object} payload - Payload from SECURE_FIELD_VALIDATION_ERROR Pigeon Event.
   */
  inValidateSecureFields = (payload) => {
    Object.keys(payload.errorResponse).forEach((key) => {
      const secureFieldDiv = document.getElementById(`s4payments__securefield__${key}`);
      if (secureFieldDiv) {
        secureFieldDiv.style.border = `2px solid ${S4Styles.COLORS.alert}`;

        const textOuterShell = document.getElementById(`s4_card_${key}`);
        if (textOuterShell) {

          textOuterShell.style['margin-left'] = '14px';
          textOuterShell.style.removeProperty('padding-top');
          textOuterShell.style.removeProperty('padding-left');

          const spanText = textOuterShell.firstChild;
          if (spanText) {
            spanText.style['font-size'] = '9px';
            spanText.style.color = S4Styles.COLORS.alert;
          }
        }
      }
    });
  }

  /**
   * Called when we have determined what the bin type is on a credit card entry as the user is typing it.
   * @param {Object} cardTypes - Payload from CARD_TYPE_DETERMINATION Pigeon Event.
   */
  displayCreditCardType = (cardTypes) => {
    const secureFieldDiv = document.getElementById(`s4payments__securefield__number`);
    const CC_ICON_ID = 'vn_cc_icon';
    const ele = document.getElementById(CC_ICON_ID);

    // if we know what the bin type is, show the icon if we have the SVG.
    if (cardTypes.options.length === 1) {
      const cardIcon = this.getCardIcon(cardTypes.options[0].type);
      if (cardIcon && !ele) {
        const div = S4Elements.createElement('div', CC_ICON_ID, { 'position': 'absolute', 'pointer-events': 'none', 'right': '10px' });
        cardIcon.style.width = '34px';
        cardIcon.style['margin-left'] = '230px';
        cardIcon.style['margin-top'] = '12px';
        div.appendChild(cardIcon);
        secureFieldDiv.prepend(div);
      }
    } else {

      // remove the icon if we no longer know.
      if (ele) {
        ele.parentNode.removeChild(ele);
      }
    }
  }

  /**
   * Called when we need to update values, but we don't need to or cannot repaint the entire vault.
   * This is called usually when event data is coming in while a user is typing in a Secure Field.
   * If you repaint the screen while a user is typing, they can never actually type anything!
   * @param {Object} secureFieldFocused - Payload from SECURE_FIELD_FOCUS Pigeon Event
   */
  updateWithNoPaint = (secureFieldFocused) => {
    // used specifically for secure fields
    if (secureFieldFocused.focus === 'IN') {
      const secureFieldDiv = document.getElementById(`s4payments__securefield__${secureFieldFocused.type}`);
      if (secureFieldDiv) {

        // styles the surrounding frame for an input field with colors and text as needed.
        const textOuterShell = document.getElementById(`s4_card_${secureFieldFocused.type}`);
        if (textOuterShell) {
          textOuterShell.style['margin-left'] = '14px';
          textOuterShell.style.removeProperty('padding-top');
          textOuterShell.style.removeProperty('padding-left');

          const spanText = textOuterShell.firstChild;
          if (spanText) {
            spanText.style['font-size'] = '9px';
          }
        }
      }
    } else {

      // the focus is leaving the text input

      const secureFieldDiv = document.getElementById(`s4payments__securefield__${secureFieldFocused.type}`);
      if (secureFieldDiv) {

        // set if the field is valid or not
        this.fieldsValid[secureFieldFocused.type] = secureFieldFocused.valid;

        // need to check if the input passed is validator or not
        if (!secureFieldFocused.valid) {
          secureFieldDiv.style.border = `2px solid ${S4Styles.COLORS.alert}`;
        } else {
          secureFieldDiv.style.border = `1px solid ${this.styleOverride.inputFields['border-color']}`;
        }

        if (secureFieldFocused.type === 'number') {

          const textOuterShell = document.getElementById('s4_card_number');

          if (textOuterShell) {

            // if there is text that has been typed in
            if (secureFieldFocused.hasValue) {

              const spanText = textOuterShell.firstChild;

              if (spanText && secureFieldFocused.valid) {
                spanText.style.color = this.styleOverride.inputFields.color ? this.styleOverride.inputFields.color : S4Styles.COLORS.grey;
              } else if (spanText && !secureFieldFocused.valid) {
                spanText.style.color = S4Styles.COLORS.alert;
              }

            } else {
              textOuterShell.style['padding-top'] = '13px';
              textOuterShell.style['padding-left'] = '14px';
              textOuterShell.style.removeProperty('margin-left');

              const spanText = textOuterShell.firstChild;
              if (spanText) {
                spanText.style['font-size'] = '16px';
                let color = this.styleOverride.inputFields['color'] ? this.styleOverride.inputFields['color'] : S4Styles.COLORS.grey;
                spanText.style.color = color;
              }
            }
          }
        }
      }
    }
  }

  /**
   * Makes a Secure Field input field that is styled.
   * @returns {DOM Element} - Returns the DOM element that makes up the a Secure Field complete card input (includes styling!)
   */
  createCardInputFields = () => {
    const currentView = S4Elements.createElement('div');

    // create the title
    // const ccTitle = S4Elements.createTypography(null, S4Locale.getLocalization(this.language).add_a_card, { 'padding-bottom': '16px', 'padding-top': '16px' }, 'subtitle1', this.styleOverride.label)
    const ccTitle = S4Elements.createTypography(null, S4Locale.getLocalization(this.language).add_a_card, { 'padding-bottom': '16px', 'padding-top': '16px' }, 'subtitle1');
    currentView.appendChild(ccTitle);

    // const numberField = S4Elements.createNumberField(this.styleOverride.inputFields)
    const numberField = S4Elements.createNumberField({ 'margin-bottom': '16px' });

    const outerStyle = {
      'position': 'absolute',
      'padding': '13px 14px',
      'pointer-events': 'none'
    };

    // const numberText = S4Elements.createTypography('s4_card_number', S4Locale.getLocalization(this.language).card_number, outerStyle, this.styleOverride.label)
    const numberText = S4Elements.createTypography('s4_card_number', S4Locale.getLocalization(this.language).card_number, outerStyle, 'subtitle2');
    numberField.appendChild(numberText);

    const rowView = S4Elements.createElement('div', null, {
      'display': 'flex',
      'flex-direction': 'row'
    });

    let expDateFieldStyleOverride = {
      ...this.styleOverride.inputFields,
      'margin-right': '16px'
    };

    const expDateField = S4Elements.createExpDateField(expDateFieldStyleOverride);
    // const expDateText = S4Elements.createTypography('s4_card_exp', S4Locale.getLocalization(this.language).exp_month_year, outerStyle, this.styleOverride.label)
    const expDateText = S4Elements.createTypography('s4_card_exp', S4Locale.getLocalization(this.language).exp_month_year, outerStyle, 'subtitle2');
    expDateField.appendChild(expDateText);

    const cvvField = S4Elements.createCVVField(this.styleOverride.inputFields);
    // const cvvText = S4Elements.createTypography('s4_card_cvv', S4Locale.getLocalization(this.language).cvv_code, outerStyle, this.styleOverride.label)
    const cvvText = S4Elements.createTypography('s4_card_cvv', S4Locale.getLocalization(this.language).cvv_code, outerStyle, 'subtitle2');
    cvvField.appendChild(cvvText);

    rowView.appendChild(expDateField);
    rowView.appendChild(cvvField);

    currentView.appendChild(numberField);
    currentView.appendChild(rowView);

    // set all the validation to be false
    this.fieldsValid.number = false;
    this.fieldsValid.exp = false;
    this.fieldsValid.cvv = false;

    return currentView
  }

  /**
   * Creates the base DOM and styling for all cards in the vault.
   * @returns {DOM Element} - Returns a DIV
   */
  createBaseCard = (deleteCard = false, base = false) => {

    let style = Object.assign({}, this.styleOverride.selectedCard);

    if ( this.currentCardUIState === S4Constants.cardUI.DELETE_CARD && base === false && deleteCard === true )
      style['background-color'] = this.styleOverride.deleteCard['background-color'] ? this.styleOverride.deleteCard['background-color'] : S4Styles.COLORS.alert;
    else
      style['background-color'] = this.styleOverride.selectedCard['background-color'] ? this.styleOverride.selectedCard['background-color'] : S4Styles.COLORS.lightGrey;

    return S4Elements.createElement('div', null, style)
  }

  /**
   * Creates a single card display on the vault that represents a vaulted credit card.
   * @param {String} id - A unique identifier for this credit card.
   * @param {String} cardType - The card type (visa, american-express, amex, discover, mastercard)
   * @param {String} cardNumber - The credit card number last four digits
   * @returns {DOM Element} - Returns the overall card DOM element that we need to attach to the vault DOM.
   */
  createCard = (id, cardType, cardNumber) => {

    // the outer shell DOM element
    const outer = S4Elements.createElement('div', null, { 'padding-bottom': '16px' });

    // the inside of the card element
    const card = this.createBaseCard(this.deletedCardSelected === id);
    outer.appendChild(card);

    // is there a card icon for the bin type
    let cardIcon;

    /**
     * Event function for when we select a card
     */
    const onSelectCard = () => {
      this.selectedCard = id;
      this.reset();
      this.paint(true);
      send(PIGEON_KEYS.CARD_UPDATE, {
        id: id,
        status: 'SELECTED'
      });
    };

    cardIcon = this.getCardIcon(cardType);

    // only run if we are in DELETE_CARD state
    if (this.currentCardUIState === S4Constants.cardUI.DELETE_CARD) {
      const subtractButton = S4SVGS.subtractIcon();

      // canceling delete a card mode, user does NOT want to delete this vaulted card.
      subtractButton.addEventListener('click', () => {
        this.reset();
        this.paint();
        send(PIGEON_KEYS.CHANGE_CARD_UI, {
          state: S4Constants.cardUI.DEFAULT
        });
      });

      card.appendChild(subtractButton);
    } else {

      // the default card is selected
      if (this.selectedCard === id) {
        const radioChecked = S4SVGS.radioButtonCheckedIcon();

        card.style.backgroundColor = this.styleOverride.selectedCard['background-color'] ? this.styleOverride.selectedCard['background-color'] : S4Styles.COLORS.lightGrey;

        radioChecked.style.fill = this.styleOverride.selectedCard['border-color'] ? this.styleOverride.selectedCard['border-color'] : radioChecked.style.fill;
        card.appendChild(radioChecked);
      } else {
        const radioUnchecked = S4SVGS.radioButtonUncheckedIcon();

        card.style['background-color'] = S4Styles.COLORS.lightGrey;
        card.style['border-color'] = this.styleOverride.inputFields['border-color'] ? this.styleOverride.inputFields['border-color'] : S4Styles.COLORS.lightGrey;

        // we can only select a card, if it is not  already selected
        radioUnchecked.addEventListener('click', onSelectCard);
        card.appendChild(radioUnchecked);
      }
    }

    const cardDetails = S4Elements.createElement('div', null, { 'display': 'flex', 'justify-content': 'center', 'flex-grow': '1' });

    // if we have an card icon, show it.
    if (cardIcon) {
      cardIcon.style.width = '34px';
      cardIcon.style['padding-right'] = '8px';
      cardDetails.appendChild(cardIcon);
    }

    // the style of the card changes, depending on the mode we are in, or if it selected or not.
    // let cardColor = this.deletedCardSelected === id ? { color: this.styleOverride.deleteCard.color ? this.styleOverride.deleteCard.color : S4Styles.COLORS.lightGrey } : { color: this.styleOverride.selectedCard.color ? this.styleOverride.selectedCard.color : null }
    let cardColor = this.deletedCardSelected === id ? { color: S4Styles.COLORS.lightGrey } : null;
    const typography = S4Elements.createTypography(null, cardNumber, null, this.selectedCard === id ? 'subtitle3' : 'subtitle2', cardColor);
    cardDetails.appendChild(typography);

    // event for when we are selecting a card, as long as we are in the right state!
    if (this.currentCardUIState !== S4Constants.cardUI.DELETE_CARD) {
      cardDetails.addEventListener('click', onSelectCard);
    }

    card.appendChild(cardDetails);

    // only activated if we are EDIT mode AND the card UI state is in the default state
    if (this.currentMode === S4Constants.mode.EDIT && this.currentCardUIState === S4Constants.cardUI.DEFAULT) {
      const deleteButton = S4SVGS.cancelIcon();

      // activate delete a card mode, we are going to ask to confirm this selection.
      deleteButton.addEventListener('click', () => {

        if (this.DEBUG_MODE) {
          console.log('Delete a Card Mode Activated');
        }

        this.deletedCardSelected = id;
        this.currentCardUIState = S4Constants.cardUI.DELETE_CARD;
        this.paint();
        send(PIGEON_KEYS.CHANGE_CARD_UI, {
          status: S4Constants.cardUI.DELETE_CARD
        });
      });

      card.appendChild(deleteButton);
    }

    // only activated if the card statee is DELETE_CARD mode
    if (this.currentCardUIState === S4Constants.cardUI.DELETE_CARD) {
      const deleteButton = S4SVGS.deleteIcon();

      // delete this card, it has been confirmed.
      deleteButton.addEventListener('click', () => {

        if (this.DEBUG_MODE) {
          console.log(`Delete this card: ${id}`);
        }

        this.reset();
        this.paint();

        // notify that we have deleted this card
        send(PIGEON_KEYS.CARD_UPDATE, {
          id: id,
          status: 'DELETED'
        });

        // update the card UI to be default so we get out of this mode.
        send(PIGEON_KEYS.CHANGE_CARD_UI, {
          status: S4Constants.cardUI.DEFAULT
        });
      });

      card.appendChild(deleteButton);
    }

    // if we show either of these, then we want to justify the content.
    if (this.currentMode === S4Constants.mode.EDIT) {
      card.style['justify-content'] = 'space-between';
    }

    card.style['padding-right'] = '14px';
    card.style['padding-left'] = '14px';

    return outer
  }

  createAddACard = () => {
    const card = this.createBaseCard(false, true);

    if (this.currentCardUIState === S4Constants.cardUI.DEFAULT) {
      card.addEventListener('click', () => {

        if (this.DEBUG_MODE) {
          console.log('Add a Credit Card Clicked');
        }

        this.currentCardUIState = S4Constants.cardUI.ADD_CARD;
        this.paint();
        send(PIGEON_KEYS.CHANGE_CARD_UI, {
          status: S4Constants.cardUI.ADD_CARD
        });
      });
    }

    const plusSvg = S4SVGS.plusIcon(this.styleOverride.addCard?.color ? this.styleOverride.addCard?.color : '#333333');
    plusSvg.style.padding = '14px';
    plusSvg.style.position = 'absolute';

    const typography = S4Elements.createTypography(null, S4Locale.getLocalization(this.language).add_a_credit_card, { 'flex-grow': 1, 'text-align': 'center ' }, 'subtitle2');

    card.appendChild(plusSvg);
    card.appendChild(typography);

    return card
  }

  /**
   * Repaints the screen after we have received data changes or local data changes have occurred. This repaints the vault in its entirety.
   * @param {Boolean} ignoreDefault - This is normally false (and is defaulted to false), but when a card is selected on the event,
   */
  paint = (ignoreDefault = false) => {

    let displayCardInputFields = false;

    const subDiv = S4Elements.createElement('div');

    if (this.cards && this.cards.length > 0) {
      this.cards.forEach(card => {

        if (!ignoreDefault && card.default && card.default === true) {
          this.selectedCard = card.uniqueId;
        }

        const tempCard = this.createCard(card.uniqueId, card.type, card.lastFour);

        subDiv.appendChild(tempCard);
      });

      if (this.currentCardUIState !== S4Constants.cardUI.ADD_CARD) {
        // show the add a card button so a user can add more credit cards to their account
        subDiv.appendChild(this.createAddACard());
      }
    } else {
      displayCardInputFields = true;
    }

    if (displayCardInputFields || this.currentCardUIState === S4Constants.cardUI.ADD_CARD) {
      subDiv.appendChild(S4Elements.createHorizontalDivider());

      // show the card input fields
      subDiv.appendChild(this.createCardInputFields());
    }

    S4UtilsDOM.replaceChildren(this.root, subDiv);
  }

  /**
   * An outside function used to override the card UI state when necessary for the vault.
   * @param {String} state - The state from constants -> cardUI (DEFAULT, ADD_CARD, DELETE_Card)
   */
  overrideCardUIState = (state) => {
    this.currentCardUIState = state;
  }

  /**
   * Get the current card UI Status - DEFAULT, ADD_CARD, DELETE_CARD
   * @returns {String} - Returns a string (enum) of constants.cardUI
   */
  getCardUIStatus = () => {
    return this.currentCardUIState
  }

  /**
   * Displays error messages for the vault as a whole. These errors will automatically be attached to the DOM.
   * @param {Boolean} pDisplay - Toggle of displaying the error, or removing the error from the screen.
   * @param {String} message - The text we want to display in the error field
   */
  displayError = (pDisplay, message) => {

    const ERROR_ID = 'vn_error_display';
    const ele = document.getElementById(ERROR_ID);

    if (pDisplay) {

      const title = S4Elements.createTypography(null, message, null, 'alert');

      if (ele) {
        S4UtilsDOM.replaceChildren(ele, title);
      } else {
        const div = S4Elements.createElement('div', ERROR_ID);
        div.appendChild(title);
        this.root.appendChild(div);
      }
    } else {
      if (ele) {
        ele.parentNode.removeChild(ele);
      }
    }
  }

  /**
   * Displays the vault display as a whole.
   * @param {Array} pCards - An array of card objects that we received from the API for the vault.
   * @param {String - enum} pMode - The mode we are in - constants.mode (EDIT or SELECT)
   * @param {String} pLanguage - The language that we are using (en-us format)
   * @param {Boolean} debug - Are we in debug mode or not.
   * @returns {DOM Element} - Returns the root <DIV> element of this vault.
   */
  display = (pCards, pMode, pLanguage, debug) => {
    console.log(`S4VaultComponents.display - ${pLanguage}`);

    this.language = pLanguage;
    this.currentMode = pMode;
    this.cards = pCards;
    this.root = S4Elements.createElement('div');
    this.DEBUG_MODE = debug;

    addReceiver(PIGEON_KEYS.SECURE_FIELD_FOCUS, (payload) => {
      this.updateWithNoPaint(payload);
      this.displayError(false);
    });

    addReceiver(PIGEON_KEYS.SECURE_FIELD_VALIDATION_ERROR, (payload) => {
      this.inValidateSecureFields(payload);
      if (payload.key && payload.key === 'DECLINED') {
        this.displayError(true, S4Locale.getLocalization(this.language).card_declined);
      }
    });

    addReceiver(PIGEON_KEYS.CARD_TYPE_DETERMINATION, (payload) => {
      this.displayCreditCardType(payload);
    });

    this.selectedCard = null;

    this.paint();

    return this.root
  }
}

/* eslint-disable valid-jsdoc */

class S4ApplePay {

  // The decoded token that was passed into the dropin
  decodedToken

  // The session for Apple Pay
  session

  // All Apple Pay Options
  applePayOptions

  // the language we are using for localization
  language

  // the prevalidation function
  preValidation

  // are we in debug mode or not.
  DEBUG_MODE = false

  // sdk mode feature object
  sdkMode

  /**
   * Attach the Apple Pay scripts to the document head.
   */
  attachApplePayScript = () => {
    const applePayScript = S4Elements.createElement('script');
    applePayScript.src = 'https://applepay.cdn-apple.com/jsapi/v1/apple-pay-sdk.js';
    document.head.appendChild(applePayScript);
  }

  /**
   * Get an Apple Pay Session
   * @param {String} validationURL - The validation URL from Apple
   * @returns {Promise} - Returns a promise
   */
  getApplePaySession = (validationURL) => {

    if (this.DEBUG_MODE) {
      console.log('Apple Pay - getApplePaySession');
      console.log(`Validation URL: ${validationURL}`);
    }

    return new Promise((resolve, reject) => {
      ajax({
        type: 'POST',
        url: `${this.decodedToken.s4paymentsMServer}/index.cfm?fuseaction=ws.applePaySession`,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded; charset=utf-8'
        },
        isFormData: true,
        data: {
          validationURL: validationURL,
          i4go_accessBlock: this.decodedToken.accessBlock
        }
      }).then((responseObj) => {

        // i4go has responsed, lets see what they have to say

        if (this.DEBUG_MODE) {
          console.log('Apple Pay - getApplePaySession - RESPONSE');
          console.log(responseObj);
        }

        // extract the response to send only what we want
        if (responseObj.status === 200) {

          const tempResponse = JSON.parse(responseObj.response);

          if (this.DEBUG_MODE) {
            console.log('*** Apple Pay Response ***');
            console.log(tempResponse);
          }

          resolve(tempResponse);

        } else {
          reject(responseObj.status);
        }
      }).catch((e) => {
        reject(e);
      });
    })
  }

  /**
   * Post the Apple Pay token to Shift4
   * @param {Object} payment - The payment object from Apple
   * @returns {Promise} - Returns a promise
   */
  postApplePayToken = (payment) => {

    if (this.DEBUG_MODE) {
      console.log('Apple Pay - postApplePayToken');
      console.log(payment);
    }

    return new Promise((resolve, reject) => {

      if (this.DEBUG_MODE) {
        console.log(payment);
      }

      ajax({
        type: 'POST',
        url: `${this.decodedToken.s4paymentsServer}/index.cfm?fuseaction=api.jsonPostCardEntry`,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded; charset=utf-8'
        },
        isFormData: true,
        data: {
          i4go_accessBlock: this.decodedToken.accessBlock,
          i4go_applePayToken: JSON.stringify(payment)
        }
      }).then((responseObj) => {

        if (this.DEBUG_MODE) {
          console.log(responseObj);
        }

        // extract the response to send only what we want
        if (responseObj.status === 200) {

          const tempResponse = JSON.parse(responseObj.response);

          if (this.DEBUG_MODE) {
            console.log('*** Apple Pay RESPONSE ***');
            console.log(tempResponse);
            console.log(tempResponse.i4go_responsetext);
          }

          resolve(tempResponse);
        } else {
          reject(responseObj.status);
        }
      }).catch((e) => {
        reject(e);
      });
    })
  }

  /**
   * Creates a base apple pay card element
   * @returns {DOM Element} - Returns a DOM Element.
   */
  createBaseCard = () => {

    const style = {
      'border-radius': '6px',
      'height': '48px',
      'display': 'flex',
      'align-items': 'center',
      'background-color': S4Styles.COLORS.lightGrey,
      'border': `1px solid ${S4Styles.COLORS.buttonBorderGrey}`
    };

    return S4Elements.createElement('div', null, style)
  }

  /**
   * Create an Apple Pay Button for WebSDK Mode only.
   * @returns {DOM Element} - Returns a DOM element for a special apple pay button for SDK Mode Only.
   */
  createSDKModeApplePayButton = () => {
    const card = this.createBaseCard();

    const icon = S4SVGS.applePayIcon();
    icon.style.padding = '14px';
    icon.style.position = 'absolute';

    const typography = S4Elements.createTypography(null, S4Locale.getLocalization(this.language).apple_pay, { 'flex-grow': 1, 'text-align': 'center '}, 'subtitle2');

    card.appendChild(icon);
    card.appendChild(typography);

    return card
  }

  /**
   * Initialize Apple Pay
   * @param {Object} pApplePayOptions - All Apple Pay options
   * @param {Object} pDecodedToken - The decoded token that was passed into the dropin.
   * @param {Boolean} pSdkMode - Is this in SDK Mode or not
   * @param {String} pLanguage - What language are we using for localization
   * @param {Function} pPreValidation - A function that is called before a wallet payment type is run (Apple Pay or Google Pay). Must return a Boolean - true if it validates, false otherwise.
   * @param {Boolean} hasBeenInitialized - Has this been initialized before or not.
   * @param {Boolean} pDebugMode - Are we in debug mode or not.
   */
  initialize = (pApplePayOptions, pDecodedToken, pSdkMode, pLanguage, pPreValidation, hasBeenInitialized, pDebugMode) => {

    this.applePayOptions = pApplePayOptions;
    this.decodedToken = pDecodedToken;
    this.sdkMode = pSdkMode;
    this.language = pLanguage;
    this.preValidation = pPreValidation;
    this.DEBUG_MODE = pDebugMode;

    if (!hasBeenInitialized) {
      this.attachApplePayScript();
    }

    const root = S4Elements.createElement('div', 's4payments-drop-in-wallet-apple-pay');
    let button;

    if (this.sdkMode && this.sdkMode.enabled) {

      button = this.createSDKModeApplePayButton();

      button.addEventListener('click', () => {
        if (this.DEBUG_MODE) {
          console.log('createSDKModeApplePayButton Clicked');
        }
        if (this.sdkMode.event) {
          this.sdkMode.event();
        }
      });

    } else {

      button = S4Elements.createElement('apple-pay-button', null, {
        '-webkit-appearance': '-apple-pay-button',
        width: '100%'
      }, {
        buttonstyle: 'black',
        type: 'buy',
        locale: 'en'
      });

      button.addEventListener('click', () => {

        if (this.preValidation && !this.preValidation()) {
          if (this.DEBUG_MODE) {
            console.log('Apple Pay - Pre Validation Failed. Will not continue.');
          }
          return
        }

        send(PIGEON_KEYS.START_WALLET_PROCESS, {
          'wallet': 'apple-pay'
        });

        let request = {
          countryCode: this.applePayOptions.countryCode ? this.applePayOptions.countryCode : 'US',
          currencyCode: this.applePayOptions.currencyCode ? this.applePayOptions.currencyCode : 'USD',
          supportedNetworks: ['visa', 'masterCard', 'amex', 'discover'],
          merchantCapabilities: ['supports3DS'],
          total: { label: this.applePayOptions.label, amount: this.applePayOptions.amount }
        };

        if (this.DEBUG_MODE) {
          console.log('REQUEST FOR APPLE PAY');
          console.log(request);
        }

        if (window.ApplePaySession) {
          if (this.DEBUG_MODE) {
            console.log('Found Apple Pay Session Object');
          }
        }

        if (ApplePaySession.supportsVersion(3)) {
          if (this.DEBUG_MODE) {
            console.log('Apple Pay Session supports version 3');
          }
        }

        if (ApplePaySession.canMakePayments()) {
          if (this.DEBUG_MODE) {
            console.log('Apple Pay Session can make payments');
          }
        }

        if (window.ApplePaySession && ApplePaySession.supportsVersion(3) && ApplePaySession.canMakePayments()) {
          this.session = new ApplePaySession(3, request);
          this.session.begin();

          if (this.DEBUG_MODE) {
            console.log('Apple Pay - Start Session');
          }

          this.session.onvalidatemerchant = function (event) {

            if (this.DEBUG_MODE) {
              console.log('Appla Pay - onvalidatemerchant');
            }

            const validationURL = event.validationURL;
            this.getApplePaySession(validationURL).then((response) => {

              if (this.DEBUG_MODE) {
                console.log('getApplePaySession - PROMISE THEN');
                console.log(response);
              }

              this.session.completeMerchantValidation(response);
              this.session.onpaymentauthorized = (authEvent) => {

                if (this.DEBUG_MODE) {
                  console.log('getApplePaySession - onpaymentauthorized');
                  console.log(authEvent);
                }

                // ready to send this to Shift4
                this.postApplePayToken(authEvent.payment).then((applePayTokenResponse) => {

                  if (this.DEBUG_MODE) {
                    console.log('postApplePayToken - PROMISE THEN');
                    console.log(applePayTokenResponse);
                  }

                  this.session.completePayment(this.session.STATUS_SUCCESS);

                  send(PIGEON_KEYS.WALLET_PAYMENT, {
                    'wallet': 'apple-pay',
                    'token': applePayTokenResponse
                  });
                }).catch((e) => {

                  this.session.completePayment(this.session.STATUS_FAILURE);

                  send(PIGEON_KEYS.WALLET_PAYMENT, {
                    'wallet': 'apple-pay',
                    'token': applePayTokenResponse
                  });
                });
              };
            });
          };

          this.session.oncancel = function () {

            if (this.DEBUG_MODE) {
              console.log('Apple Pay - Session Cancel');
            }

            send(PIGEON_KEYS.WALLET_CHANGE, {
              'wallet': 'apple-pay',
              'session': 'canceled'
            });
          };

        } else {

          if (this.DEBUG_MODE) {
            console.log('Apple Pay Session is not supported');
          }

          send(PIGEON_KEYS.WALLET_CHANGE, {
            'wallet': 'apple-pay',
            'session': 'not_supported'
          });
        }
      });
    }

    root.appendChild(button);

    return root
  }
}

class S4GooglePay {

  /**
   * Initialize Google Pay
   * @param {Object} pGooglePayOptions - All Google Pay options
   * @param {Object} pDecodedToken - The decoded token that was passed into the dropin.
   * @param {Boolean} pSdkMode - Is this in SDK Mode or not
   * @param {String} pLanguage - What language are we using for localization
   * @param {Function} pPreValidation - A function that is called before a wallet payment type is run (Apple Pay or Google Pay). Must return a Boolean - true if it validates, false otherwise.
   * @param {Boolean} hasBeenInitialized - Has this been initialized before or not.
   * @param {Boolean} pDebugMode - Are we in debug mode or not.
   */
  initialize = (pGooglePayOptions, pDecodedToken, pSdkMode, pLanguage, pPreValidation, hasBeenInitialized, pDebugMode) => {

  }
}

/* eslint-disable valid-jsdoc */

class S4Wallet {

  /**
   * Initialize the wallet for apple pay and/or google pay.
   * @param {DOM Element} walletRoot - the root component to attach to
   * @param {String} language - What language are we using for localization
   * @param {Object} decodedToken - The decoded token that was passed into the dropin.
   * @param {Boolean} pSdkMode - Is this in SDK Mode or not
   * @param {Object} applePayOptions - All Apple Pay options
   * @param {Object} googlePayOptions - All Google Pay options
   * @param {Function} preValidation - A function that is called before a wallet payment type is run (Apple Pay or Google Pay). Must return a Boolean - true if it validates, false otherwise.
   * @param {Boolean} hasBeenInitialized - Has this been initialized before or not.
   * @param {Boolean} DEBUG_MODE - Are we in debug mode or not.
   */
  initialize = (walletRoot, language, decodedToken, pSdkMode, applePayOptions, googlePayOptions, preValidation, hasBeenInitialized, DEBUG_MODE) => {

    if (applePayOptions) {
      const applePay = new S4ApplePay();
      walletRoot.appendChild(applePay.initialize(applePayOptions, decodedToken, pSdkMode, language, preValidation, hasBeenInitialized, DEBUG_MODE));
    }

    // Stubbed in for now. Google Pay currently not available.
    if (googlePayOptions) {
      const googlePay = new S4GooglePay();
      walletRoot.appendChild(googlePay.initialize(googlePayOptions, decodedToken, pSdkMode, language, preValidation, hasBeenInitialized, DEBUG_MODE));
    }
  }
}

class Payments extends Component {

  // a check to see if the drop in has been initialized before or not before.
  hasBeenInitialized = false

  // #creditCard = undefined
  // #vault = undefined
  // #contact = undefined // coming soon, not needed for VenueNext
  // #wallet = undefined
  #root = undefined
  #container = undefined

  // the vault root div that the vault attaches to
  vaultRoot = undefined

  // the wallet root div that the wallet attaches to
  walletRoot = undefined

  // An array of all our vaulted cards.
  vaultedCards = undefined

  // what mode are we in
  currentMode = S4Constants.mode.SELECT

  // what frames have been initialized. An Object.
  framesInitialized = {}

  // What frames are we initializing with, defaults
  framesToInitialize = ['number', 'exp', 'cvv']

  vaultComponents

  // if risk is used, this will be populated once the risk session is determined
  riskSessionId

  // if 3ds is necessary, this is populated during its ready
  threeDSBrowserInfo

  // if 3ds is necessary, this is populated during its ready
  threeDSCompletionUrl

  /**
   *
   * @param {*} configs
   * @param {*} env
   * @param {*} debugMode
   * @returns
   */
  constructor(configs, componentsConfigs, globalConfigs, env, debugMode) {
    super(configs, componentsConfigs, globalConfigs, env, debugMode);

    // if the configs did not validate, do not continue
    if (!this.configs) {
      return
    }

    // need to add payments, this tells the S4PaymentsWebSDK that we are using
    // a payments enabled architecture. Secure Fields
    if (!this.configs.services) {
      this.configs.services = ['payments'];
    } else {
      this.configs.services.push('payments');
    }

    if (debugMode) {
      console.log("Payments");
    }

    this.vaultComponents = new S4VaultComponents( configs.styleOverrides );
  }

  /**
   * Used to display the vault or not that has already been placed on the DOM.
   * @param {Boolean} pDisplay - True if you want to display the vault, false otherwise.
   */
  displayVault(pDisplay) {
    if (pDisplay) {
      this.vaultRoot.style.display = 'block';
    } else {
      this.vaultRoot.style.display = 'none';
    }
  }

  /**
   * Used to display the wallet or not that has already been placed on the DOM.
   * @param {Boolean} pDisplay - True if you want to display the wallet, false otherwise.
   */
  displayWallet(pDisplay) {
    // if (pDisplay) {
    //   this.walletRoot.style.display = 'block'
    // } else {
    //   this.walletRoot.style.display = 'none'
    // }
  }

  /**
   * Remove a locally vaulted credit card from memory.
   * @param {String} id - The uniqueId of a vaulted credit card
   */
  removeLocalVaultedCard(id) {
    for (let i = 0; i < this.vaultedCards.length; i++) {
      if (this.vaultedCards[i].uniqueId === id) {
        this.vaultedCards.splice(i, 1);
        break
      }
    }
  }

  /**
   * Update a locally vaulted credit card from memory to be default.
   * @param {String} id - The uniqueId of a vaulted credit card
   */
  updateDefaultLocalVaultedCard(id) {
    for (let i = 0; i < this.vaultedCards.length; i++) {

      // find the previous default card and set it to false, no longer default
      if (this.vaultedCards[i].default) {
        this.vaultedCards[i].default = false;
      }

      // when we find the new card, set is as default
      if (this.vaultedCards[i].uniqueId === id) {
        this.vaultedCards[i].default = true;
      }
    }
  }

  setup() {

    // Send a message when we focus IN or OUT on a Secure Field.
    S4Payments.bus.receive(S4Payments.events.SECURE_FIELD_FOCUS, (payload) => {

      if (this.DEBUG_MODE) {
        console.log('***** S4Payments.bus.receive - SECURE_FIELD_FOCUS *****');
        console.log(payload);
      }

      send(PIGEON_KEYS.SECURE_FIELD_FOCUS, payload);
    }, 'VN_WEB_DROP_IN');

    // When a Secure Field does not validate for any reason
    S4Payments.bus.receive(S4Payments.events.VALIDATION_ERROR, (payload) => {

      if (this.DEBUG_MODE) {
        console.log('***** S4Payments.bus.receive - VALIDATION_ERROR *****');
        console.log(payload);
      }

      if (payload.error) {
        send(PIGEON_KEYS.SECURE_FIELD_VALIDATION_ERROR, payload);

        // if the integrator has setup to receive event notifications
        if (this.configs.events) {
          this.configs.events({ event: PIGEON_KEYS.SECURE_FIELD_VALIDATION_ERROR, ...payload });
        }
      }
    }, 'VN_WEB_DROP_IN');

    // What the credit card type is
    S4Payments.bus.receive(S4Payments.events.CARD_TYPE_DETERMINATION, (payload) => {

      if (this.DEBUG_MODE) {
        console.log('***** S4Payments.bus.receive - CARD_TYPE_DETERMINATION *****');
        console.log(payload);
      }

      send(PIGEON_KEYS.CARD_TYPE_DETERMINATION, payload);
    }, 'VN_WEB_DROP_IN');

    // When a Secure Field frame is done rendering and setting up, then we need to send out letting everyone know we are done. Sends along the frame name and frame ID.
    S4Payments.bus.receive(S4Payments.events.FRAME_INITIALIZED, (payload) => {

      if (this.DEBUG_MODE) {
        console.log('***** S4Payments.bus.receive - FRAME_INITIALIZED *****');
        console.log(payload);
      }

      // what frame name initialized
      this.framesInitialized[payload.frameName] = true;

      // need to check all the frames to see if they all initialized
      let allInitialized = true;

      // if we find one that is not initialized, then we aren't fully loaded yet.
      for (let i = 0; i < this.framesToInitialize.length; i++) {
        if (!this.framesInitialized[this.framesToInitialize[i]]) {
          allInitialized = false;
          break
        }
      }

      // fully loaded, remove everything!
      if (allInitialized) {
        this.displayVault(true);
        this.displayWallet(true);
        S4Loaders.removeLoader();

        // if the integrator has setup to receive event notifications
        if (this.configs.events) {
          this.configs.events({ event: PIGEON_KEYS.S4PAYMENTS_READY });
        }
      }
    }, 'VN_WEB_DROP_IN');

    // When all cards are retrieved for a specific user
    addReceiver(PIGEON_KEYS.VAULT_RETRIEVED_ALL_CARDS, (payload) => {

      if (this.DEBUG_MODE) {
        console.log('***** PIGEON_KEYS.VAULT_RETRIEVED_ALL_CARDS *****');
        console.log(payload);
      }

      this.vaultedCards = payload.cards;
      this.vaultComponents.overrideCardUIState(payload.cards.length > 0 ? S4Constants.cardUI.DEFAULT : S4Constants.cardUI.ADD_CARD);
      this.paint();
      this.initializeSecureFields();
      this.displayVault(true);
      this.displayWallet(true);
      S4Loaders.removeLoader();

      // if the integrator has setup to receive event notifications
      if (this.configs.events) {
        this.configs.events({ event: PIGEON_KEYS.VAULT_RETRIEVED_ALL_CARDS, ...payload });
      }

      // if the integrator has setup to receive event notifications
      if (this.configs.events) {
        this.configs.events({ event: PIGEON_KEYS.S4PAYMENTS_READY });
      }
    });

    // When a card is vaulted successfully
    addReceiver(PIGEON_KEYS.VAULT_CARD_VAULTED, (payload) => {

      if (this.DEBUG_MODE) {
        console.log('***** PIGEON_KEYS.VAULT_CARD_VAULTED *****');
        console.log(payload);
      }

      // need to refetch vaulted cards
      S4Vault.getVaultedCardsByUser(this.configs.token, this.decodedToken.vaultServer);

      // if the integrator has setup to receive event notifications
      if (this.configs.events) {
        this.configs.events({ event: PIGEON_KEYS.VAULT_CARD_VAULTED, ...payload });
      }
    });

    // When a card has been deleted in the vault successfully.
    addReceiver(PIGEON_KEYS.VAULT_CARD_DELETED, (payload) => {

      if (this.DEBUG_MODE) {
        console.log('***** PIGEON_KEYS.VAULT_CARD_DELETED *****');
        console.log(payload);
      }

      // need to remove it from the local memory
      this.removeLocalVaultedCard(payload.card.uniqueId);

      // repaint the screen so the card no longer shows up
      this.paint();

      // if there are no more vaulted cards, we are going to be showing the credit card input fields
      // therefore we need to initialize the secure fields
      if (this.vaultedCards.length <= 0) {
        this.initializeSecureFields();
        this.vaultComponents.overrideCardUIState(S4Constants.cardUI.ADD_CARD);
      }

      // need to refetch vaulted cards
      S4Vault.getVaultedCardsByUser(this.configs.token, this.decodedToken.vaultServer);

      // if the integrator has setup to receive event notifications
      if (this.configs.events) {
        this.configs.events({ event: PIGEON_KEYS.CARD_UPDATE, ...payload });
      }
    });

    // listen for all card event updates
    addReceiver(PIGEON_KEYS.CARD_UPDATE, (payload) => {

      if (this.DEBUG_MODE) {
        console.log('***** PIGEON_KEYS.CARD_UPDATE *****');
        console.log(payload);
      }

      if (payload.status === 'DELETED') {
        if (this.configs.vault) {
          S4Vault.deleteCard(payload.id, this.configs.token, this.decodedToken.vaultServer);
        }
      } else if (payload.status === 'SELECTED') {
        if (this.configs.vault) {
          this.updateDefaultLocalVaultedCard(payload.id);
          S4Vault.updateCard({ uniqueId: payload.id, default: true }, this.configs.token, this.decodedToken.vaultServer);
        }
      }

      // if the integrator has setup to receive event notifications
      if (this.configs.events) {
        this.configs.events({ event: PIGEON_KEYS.CARD_UPDATE, ...payload });
      }
    });

    // listen for when a card has been updated on the server
    addReceiver(PIGEON_KEYS.VAULT_CARD_UPDATED, (payload) => {

      if (this.DEBUG_MODE) {
        console.log('***** PIGEON_KEYS.VAULT_CARD_UPDATED *****');
        console.log(payload);
      }

      // if the integrator has setup to receive event notifications
      if (this.configs.events) {
        this.configs.events({ event: PIGEON_KEYS.VAULT_CARD_UPDATED, ...payload });
      }
    });

    // Anytime the card UI changes; such as when changing from DELETE mode to DEFAULT mode.
    addReceiver(PIGEON_KEYS.CHANGE_CARD_UI, (payload) => {

      if (this.DEBUG_MODE) {
        console.log('***** PIGEON_KEYS.CHANGE_CARD_UI *****');
        console.log(payload);
      }

      // only handle the client init for the library if we are showing the secure fields
      if (payload.status === S4Constants.cardUI.ADD_CARD) {
        this.initializeSecureFields();
      }

      // if the integrator has setup to receive event notifications
      if (this.configs.events) {
        this.configs.events({ event: PIGEON_KEYS.CHANGE_CARD_UI, ...payload });
      }
    });

    // An Apple Pay or Google Pay wallet payment
    addReceiver(PIGEON_KEYS.WALLET_PAYMENT, (payload) => {

      if (this.DEBUG_MODE) {
        console.log('***** PIGEON_KEYS.WALLET_PAYMENT *****');
        console.log(payload);
      }

      // if the integrator has setup to receive event notifications
      if (this.configs.events) {
        this.configs.events({ event: PIGEON_KEYS.WALLET_PAYMENT, ...payload });
      }
    });

    // An Apple Pay or Google Pay wallet change event like 'cancel' or 'not_supported'
    addReceiver(PIGEON_KEYS.WALLET_CHANGE, (payload) => {

      if (this.DEBUG_MODE) {
        console.log('***** PIGEON_KEYS.WALLET_CHANGE *****');
        console.log(payload);
      }

      // if the integrator has setup to receive event notifications
      if (this.configs.events) {
        this.configs.events({ event: PIGEON_KEYS.WALLET_CHANGE, ...payload });
      }
    });

    // Called when a payment is started from the wallet; this would be apple pay or google pay.
    addReceiver(PIGEON_KEYS.START_WALLET_PROCESS, (payload) => {

      if (this.DEBUG_MODE) {
        console.log('***** PIGEON_KEYS.START_WALLET_PROCESS *****');
        console.log(payload);
      }

      // if the integrator has setup to receive event notifications
      if (this.configs.events) {
        this.configs.events({ event: PIGEON_KEYS.START_WALLET_PROCESS, ...payload });
      }
    });
  }

  /**
   * This is used after we have manipulated the DOM so we can re-paint the screen with updates.
   */
  paint() {
    S4UtilsDOM.replaceChildren(this.vaultRoot, this.vaultComponents.display(this.vaultedCards, this.currentMode, this.globalConfigs.locale, this.DEBUG_MODE));
  }

  /**
   * This is used to initialize the Secure Fields system that is with the s4payments client s4payments-web-sdk.
   */
  initializeSecureFields() {

    if (this.DEBUG_MODE) {
      console.log('this.initializeSecureFields');
    }

    // if we are going to override the defaults, do it
    this.framesToInitialize = this.configs.fields?.creditCard ? this.configs.fields?.creditCard : this.framesToInitialize;

    if (this.decodedToken) {

      // create the configuration that we'll use to pass to initiailize the S4Payments library
      let sdkConfig = {
        s4paymentsServer: this.decodedToken.s4paymentsServer,
        accessBlock: this.decodedToken.accessBlock, 
        secureFieldsServer: this.decodedToken.secureFieldsServer,
        fields: this.framesToInitialize,
        token: this.configs.token,
        currencyCode: this.configs.currencyCode,
        risk: this.configs.risk,
        vnapi: this.decodedToken.vnapi,
        vault: this.configs.vault ? this.configs.vault : false, // a bool if you want to use the vault or not
        debug: this.DEBUG_MODE,
        enabledServices: this.configs.services,
        appId: this.globalConfigs.appId,
        events: (payload) => {
          console.log('INSIDE PAYMENTS EVENTS');
          console.log(payload);

          // listen for the risk ready event
          if (payload.event === "RISK_ASSESSMENT_READY") {

            // if they are wanting to use risk...
            if (this.configs.services?.includes('risk')) {
              this.riskSessionId = payload.riskSessionId;
            }
          } else if (payload.event === 'S4PAYMENTS_INITIALIZED') {
            if (this.DEBUG_MODE) {
              console.log(`S4PAYMENTS_INITIALIZED`);
              console.log(payload);
            }
            this.threeDSBrowserInfo = payload.threeDSBrowserInfo;
            this.threeDSCompletionUrl = payload.completionUrl;
          }
        }
      };

      // if we have a risk ID
      if (this.configs.services?.includes('risk') && this.configs.riskId) {
        sdkConfig.riskId = this.configs.riskId;
      }

      // need to include the development mode
      if (this.globalConfigs.development) {
        sdkConfig.development = this.globalConfigs.development;
      }

      if (this.DEBUG_MODE) {
        console.log("Initializing S4PaymentsWebSDK");
        console.log(sdkConfig);
      }

      // initialize the web
      S4Payments.sdk.initialize(sdkConfig);
    } else {
      console.log('Token provided is invalid, please try again.');
    }
  }

  /**
   *
   * @param {*} container
   */
  async mount(container) {
    await super.mount(container);

    this.setup();

    // create the root element that we will attach everything to
    this.#root = S4Elements.createElement('div', 's4payments-drop-in-root', { margin: '24px' });

    // this.#creditCard = new CreditCard(this.configs, super.getEnv(), this.DEBUG_MODE)

    // if (this.configs.vault) {
    //   this.#vault = new Vault(this.configs, super.getEnv(), this.DEBUG_MODE)
    //   this.#vault.mount(this.#root)
    // }

    // if (this.configs.wallet) {
    //   this.#wallet = new Wallet(this.configs, super.getEnv(), this.DEBUG_MODE)
    // }

    // this.#creditCard.mount()

    // find the root injection site
    this.#container = document.getElementById(container);

    // if we cannot find the injection site, error out
    if (!this.#container) {
      if (this.DEBUG_MODE) {
        console.log(`Container element not found, please try again.`);
      }
      return
    }

    // setup the wallet if there is any configured
    if (this.configs.wallet) {
      this.walletRoot = S4Elements.createElement('div', 's4payments-drop-in-wallet-root', { 'padding-bottom': '16px' });

      const wallet = new S4Wallet();
      wallet.initialize(this.walletRoot, this.globalConfigs.locale, this.decodedToken, this.configs.wallet.sdkMode, this.configs.wallet.applePay, this.configs.wallet.googlePay, this.configs.wallet.preValidation, this.hasBeenInitialized, this.DEBUG_MODE);

      this.displayWallet(false);
      this.#root.appendChild(this.walletRoot);
    }

    if (this.configs.wallet?.sdkMode && !this.configs.wallet?.sdkMode.enabled && this.configs.wallet?.sdkMode.isApplePayOnly) {
      this.displayWallet(true);
      // attach the root component to the outside container
      this.#container.appendChild(this.#root);
      return
    }

    this.vaultRoot = S4Elements.createElement('div', 's4payments-drop-in-vault-root');

    // initially hide it until we are ready to display it
    this.displayVault(false);

    this.#root.appendChild(this.vaultRoot);

    this.#root.prepend(S4Loaders.makeLoader());

    this.currentMode = S4Constants.mode.EDIT;

    // attach the root component to the outside container
    this.#container.appendChild(this.#root);

    this.paint();

    // do we need to check for vaulted cards
    // TODO: Vaulting is turned off, needs to be configurable
    // if (this.configs.vault) {
      console.log(this.configs);
      console.log(this.configs.token);
      S4Vault.getVaultedCardsByUser(this.configs.token, this.decodedToken.vaultServer);
    // }

    // set the dropin to have been initialized.
    this.hasBeenInitialized = true;
  }
}

class Components {

  // Is DEBUG enabled or not
  #DEBUG_MODE = false

  #componentsConfigs = undefined

  #globalConfigs = undefined

  #env = undefined

  /**
   * 
   * @param {Object} configs 
   * @param {Boolean} debugMode 
   */
  constructor(configs, globalConfigs, env, debugMode) {
    console.log('Components');
    this.#DEBUG_MODE = debugMode;
    this.#env = env;
    this.#globalConfigs = globalConfigs;

    // verify the locale is supported
    if (configs.locale) {
      if (!S4Locale.isSupported(configs.locale)) {
        console.log(`We currently do not support that locale, defaulting to en-us.`);
        configs.locale = 'en-us';
      }
    }

    // setup all the style utilizies
    S4StylesUtils.createLoadingAnimation();
    S4StylesUtils.createBaseApplePayStyle();
    S4StylesUtils.attachGlobalFonts();

    this.#componentsConfigs = configs;
  }

  /**
   * 
   * @param {*} name 
   * @param {*} configs 
   * @returns 
   */
  initialize(name, configs) {
    console.log('Components.initialize');

    // verify that we have the required fields

    if (!name) {
      console.log('No name provided, please try again.');
      return
    }

    if (!configs) {
      console.log('No configs provided, please try again.');
      return
    }

    let component = undefined;

    switch(name) {
      case "payments":
        component = new Payments(configs, this.#componentsConfigs, this.#globalConfigs, this.#env, this.#DEBUG_MODE);
        break
      default:
        console.log(`The component ${name} does not exist, please try again.`);
    }

    if (!component.configs) {
      console.log(`The component initialization failed, please check for errors and try again.`);
      return
    }

    return component
  }

  update(configs) {
    console.log('Components.update');
  }
}

/**
 *
 */
class Shift4 {

  // Is DEBUG enabled or not
  #DEBUG_MODE = false

  // The AppID that is set for this instance
  #appId = undefined

  // The overall configs for Shift4
  #configs = undefined

  // the locale
  #locale = "en-us"

  // what environment are we set to
  #env = 'prd'

  // The default theme
  #defaultTheme = 'shift4'

  // the Components within here is a Singleton
  #_components = undefined

  /**
   *
   * @param {*} appId
   * @param {*} configs
   */
  constructor(appId, configs = {}) {

    if (!appId) {
      console.log(`appId is required, please re-initialize and try again.`);
      return
    }

    this.#appId = appId;
    this.#configs = configs;
    this.#configs.appId = appId;

    if (this.#configs?.debug) {
      this.#DEBUG_MODE = this.#configs.debug;

      if (this.#DEBUG_MODE) {
        console.log(`Debug Mode Enabled`);
      }
    }

    if (this.#DEBUG_MODE) {
      console.log(`AppID: ${this.#appId}`);
    }

    if (this.#configs?.locale) {
      this.#locale = this.#configs.locale;

      if (this.#DEBUG_MODE) {
        console.log(`Locale: ${this.#locale}`);
      }
    }

    if (this.#configs?.development?.mode) {
      this.#env = this.#configs.development.mode;

      if (this.#DEBUG_MODE) {
        console.log(`Environment: ${this.#env}`);
      }
    }
  }

  /**
   *
   * @param {Object} configs
   * @returns Components object
   */
  components(configs) {

    if (this.#_components) {
      return this.#_components
    }

    let tempConfigs;

    // if no configs are coming in, set the defaults
    if (!configs) {
      tempConfigs = {
        locale: this.#locale,
        style: {
          theme: this.#defaultTheme
        }
      };
    } else {
      tempConfigs = configs;

      // if there is no locale, set the default
      if (!tempConfigs.locale) {
        tempConfigs.locale = this.#locale;
      }
    }

    this.#_components = new Components(tempConfigs, this.#configs, this.#env, this.#DEBUG_MODE);
    return this.#_components
  }

  internalTransaction(component, configs) {
    return new Promise((resolve, reject) => {
      if (component instanceof Payments) {

        // TODO: Removing vaulting for now
        // if the user is not trying to add a card, just return the default card
        // if (component.configs.vault && component.vaultComponents.getCardUIStatus() === S4Constants.cardUI.DEFAULT) {
        //   for (let i = 0; i < component.vaultedCards.length; i++) {
        //     if (component.vaultedCards[i].default) {
        //       resolve(component.vaultedCards[i])
        //     }
        //   }
        // } else if (component.vaultComponents.getCardUIStatus() === S4Constants.cardUI.ADD_CARD) {
        if (component.vaultComponents.getCardUIStatus() === S4Constants.cardUI.ADD_CARD) {
          S4Payments.sdk.tokenize(configs, payload => {
            // check to see if there is an error on the payload
            if (!payload.error) {

              // NO ERROR, DO SOMETHING
              if (this.#DEBUG_MODE) {
                console.log(`Tokenization Success`);
                console.log(payload);
              }

              // if (component.configs.vault) {

              //   // vault the card
              //   S4Vault.vaultCard(payload, component.configs.token, component.decodedToken.vaultServer).then(() => {
              //     console.log('after vaulting')
              //     console.log(payload)
              //     resolve(payload)
              //   }).catch(() => {
              //     // even if we couldn't vault the card, we received the card back from Shift4 so process it
              //     console.log('after vaulting - catch error')
              //     console.log(payload)
              //     resolve(payload)
              //   })
              // } else {
              //   console.log('no vaulting')
                console.log(payload);
                resolve(payload);
              // }
            } else {

              // ERROR OCCURRED, DO SOMETHING ELSE
              if (this.#DEBUG_MODE) {
                console.log(`Tokenization Error`);
                console.log(payload.error);
              }

              reject(payload.error);
            }
          });
        }
      } else {
        reject('The component you passed in cannot tokenize anything. Please try again.');
      }
    })
  }

  /**
   *
   * @param {*} component
   * @param {*} configs
   */
  tokenize(component, configs) {
    return this.internalTransaction(component, { ...configs, transactionType: 'tokenize' })
  }

  sale(component, configs) {
    return this.internalTransaction(component, { ...configs, transactionType: 'sale' })
  }

  authorize(component, configs) {
    return this.internalTransaction(component, { ...configs, transactionType: 'authorize' })
  }
}

module.exports = Shift4;
